"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.oshcutPostRoutes = exports.oshcutGetRoutes = void 0;
var _inventoryFilters = require("./inventoryFilters");
var _routeClasses = require("./routeClasses");
/**
 * All routes for OSH Cut's API that use the GET method.
 */
const oshcutGetRoutes = exports.oshcutGetRoutes = {
  /**
   * Retrieve customer-allowed properties of all material types, sheets, and tubes, in a single request. The response is
   * an object with three properties (the types, sheets, and tubes) which must be "unpacked" using `fromTupleMaterials`.
   */
  '/api/v2/materials': new _routeClasses.RouteGet()
};
const accAccountRoutes = {
  /** Gets an account. */
  '/api/v3/account/get': new _routeClasses.RoutePostForUseModel({
    model: 'account',
    filter: (accounts, params) => accounts.filter(t => t.id === params.id),
    tryFilter: true
  }),
  /** Gets the accounts that can be used in transactions */
  '/api/v3/account/get_admin_selectable_bank_accounts': new _routeClasses.RoutePost(),
  /** Gets several accounts by id. */
  '/api/v3/account/ids': new _routeClasses.RoutePostForUseModel({
    model: 'account',
    filter: (accounts, params) => accounts.filter(t => params.ids.some(id => t.id === id)),
    key: 'ids'
  }),
  /** Gets all accounts. */
  '/api/v3/account/all': new _routeClasses.RoutePostForUseModel({
    model: 'account'
  }),
  /** Creates an account. */
  '/api/v3/account/create': new _routeClasses.RoutePostForUseMutator(),
  /** Updates an account. */
  '/api/v3/account/update': new _routeClasses.RoutePostForUseMutator()
};
const accItemRoutes = {
  /** Gets all accounting items */
  '/api/v3/acc_item/all': new _routeClasses.RoutePost(),
  /** Consumes non-WIP FIFO inventory. */
  '/api/v3/acc_item/consume_fifo_inventory': new _routeClasses.RoutePostForUseMutator(),
  /** Create a new accounting item */
  '/api/v3/acc_item/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes an accounting item */
  '/api/v3/acc_item/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Gets a set of acc items for a given sheet ID */
  '/api/v3/acc_item/for_material_sheet_id': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (accItems, params) => accItems.filter(accItem => accItem.material_sheet_id === params.material_sheet_id && accItem.type === 'metal_sheet')
  }),
  /** Gets a set of acc items for a given tube ID */
  '/api/v3/acc_item/for_material_tube_id': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (accItems, params) => accItems.filter(accItem => accItem.material_tube_id === params.material_tube_id && accItem.type === 'metal_tube')
  }),
  /** Gets a set of acc items for a given set of tube IDs */
  '/api/v3/acc_item/for_material_tube_ids': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (accItems, params) => accItems.filter(a => params.material_tube_ids.includes(a.material_tube_id)),
    key: 'material_tube_ids'
  }),
  /** Gets a set of acc items for a given set of sheet IDs */
  '/api/v3/acc_item/for_material_sheet_ids': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (accItems, params) => accItems.filter(a => params.material_sheet_ids.includes(a.material_sheet_id)),
    key: 'material_sheet_ids'
  }),
  /** Gets a set of acc items based on a list of part ids */
  '/api/v3/acc_item/for_parts': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (accItems, params) => accItems.filter(a => params.part_ids.includes(a.part_id)),
    key: 'part_ids'
  }),
  /** Gets items by sku. */
  '/api/v3/acc_item/for_sku': new _routeClasses.RoutePost(),
  /** Gets items by upc code. */
  '/api/v3/acc_item/for_upc_code': new _routeClasses.RoutePost(),
  /** Return a single AccItem by id. */
  '/api/v3/acc_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (items, params) => items.filter(item => item.id === params.id),
    tryFilter: true
  }),
  /** Gets a set of acc items based on a list of parts */
  '/api/v3/acc_item/get_for_parts': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (accItems, params) => accItems.filter(a => params.part_ids.includes(a.part_id)),
    key: 'part_ids'
  }),
  /** Return several AccItems by their ids. */
  '/api/v3/acc_item/ids': new _routeClasses.RoutePostForUseModel({
    model: 'acc_item',
    filter: (items, params) => items.filter(item => params.ids.includes(item.id)),
    key: 'ids'
  }),
  /** Performs a paginated search of acc_items. */
  '/api/v3/acc_item/paginated_search': new _routeClasses.RoutePost(),
  /** Update an AccItem. */
  '/api/v3/acc_item/update': new _routeClasses.RoutePostForUseMutator()
};
const accPurchaseOrderRoutes = {
  /** Fetch purchase orders */
  '/api/v3/acc_purchase_order/find': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order',
    filter: (accPurchaseOrders, params) => accPurchaseOrders.filter(po => {
      if (!!po.deleted) return false;
      const {
        start_timestamp,
        end_timestamp,
        statuses,
        vendor_id
      } = params;
      const openStatuses = ['draft', 'submitted', 'confirmed', 'backordered', 'partially_received'];

      // Clamp date range to start_timestamp and end_timestamp (if provided), but include open POs outside of the date range
      if (start_timestamp || end_timestamp) {
        let isWithinRange = true;
        if (start_timestamp && po.creation_date < start_timestamp) {
          isWithinRange = false;
        }
        if (end_timestamp && po.creation_date > end_timestamp) {
          isWithinRange = false;
        }
        const isOpen = openStatuses.includes(po.status);
        if (!(isOpen || isWithinRange)) {
          return false;
        }
      }

      // Match by other params
      if (statuses && !statuses.includes(po.status)) return false;
      if (vendor_id && po.vendor_id !== vendor_id) return false;
      return true;
    })
  }),
  /** Get a purchase order */
  '/api/v3/acc_purchase_order/get': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order',
    filter: (accPurchaseOrders, params) => accPurchaseOrders.filter(a => a.id === params.id),
    tryFilter: true
  }),
  /** Gets several purchase orders by their IDs */
  '/api/v3/acc_purchase_order/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order',
    filter: (accPurchaseOrders, params) => accPurchaseOrders.filter(a => params.ids.includes(a.id)),
    key: 'ids'
  }),
  /** Create a purchase order */
  '/api/v3/acc_purchase_order/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a purchase order */
  '/api/v3/acc_purchase_order/update': new _routeClasses.RoutePostForUseMutator()
};
const accPurchaseOrderItemRoutes = {
  /** Get purchase order items related to a purchase order */
  '/api/v3/acc_purchase_order_item/for_po': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order_item',
    filter: (accPurchaseOrderItems, params) => accPurchaseOrderItems.filter(a => a.acc_purchase_order_id === params.purchase_order_id)
  }),
  /** Get a purchase order item related to a set of purchase orders */
  '/api/v3/acc_purchase_order_item/for_pos': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order_item',
    filter: (accPurchaseOrderItems, params) => accPurchaseOrderItems.filter(a => params.purchase_order_ids.includes(a.acc_purchase_order_id)),
    key: 'purchase_order_ids'
  }),
  /** Gets purchase order items based on a set of filters. Always excludes received and deleted items. */
  '/api/v3/acc_purchase_order_item/find': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order_item',
    filter: (accPurchaseOrderItems, params) => accPurchaseOrderItems.filter(poItem => {
      var _params$acc_purchase_, _params$item_ids, _params$item_types, _params$material_tube, _params$material_shee;
      if (!!poItem.deleted) return false;
      if (params.acc_purchase_order_ids && ((_params$acc_purchase_ = params.acc_purchase_order_ids) === null || _params$acc_purchase_ === void 0 ? void 0 : _params$acc_purchase_.length) > 0) {
        if (!params.acc_purchase_order_ids.includes(poItem.acc_purchase_order_id)) return false;
      }
      if (params.item_ids && ((_params$item_ids = params.item_ids) === null || _params$item_ids === void 0 ? void 0 : _params$item_ids.length) > 0) {
        if (!params.item_ids.includes(poItem.acc_item_id)) return false;
      }
      if (params.item_types && ((_params$item_types = params.item_types) === null || _params$item_types === void 0 ? void 0 : _params$item_types.length) > 0) {
        if (!params.item_types.includes(poItem.item_type)) return false;
      }
      if (params.material_tube_ids && ((_params$material_tube = params.material_tube_ids) === null || _params$material_tube === void 0 ? void 0 : _params$material_tube.length) > 0) {
        var _poItem$material_tube;
        if (!params.material_tube_ids.includes((_poItem$material_tube = poItem.material_tube_id) !== null && _poItem$material_tube !== void 0 ? _poItem$material_tube : 0)) return false;
      }
      if (params.material_sheet_ids && ((_params$material_shee = params.material_sheet_ids) === null || _params$material_shee === void 0 ? void 0 : _params$material_shee.length) > 0) {
        var _poItem$material_shee;
        if (!params.material_sheet_ids.includes((_poItem$material_shee = poItem.material_sheet_id) !== null && _poItem$material_shee !== void 0 ? _poItem$material_shee : 0)) return false;
      }
      return true;
    })
  }),
  /** Get a single purchase order item */
  '/api/v3/acc_purchase_order_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order_item',
    filter: (accPurchaseOrderItems, params) => accPurchaseOrderItems.filter(a => a.id === params.id),
    tryFilter: true
  }),
  /** Gets multiple purchase order items */
  '/api/v3/acc_purchase_order_item/ids': new _routeClasses.RoutePostForUseModel({
    model: 'acc_purchase_order_item',
    filter: (accPurchaseOrderItems, params) => accPurchaseOrderItems.filter(a => params.ids.includes(a.id))
  }),
  /** Create a purchase order item */
  '/api/v3/acc_purchase_order_item/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a purchase order item */
  '/api/v3/acc_purchase_order_item/update': new _routeClasses.RoutePostForUseMutator()
};
const accountStatementRoutes = {
  '/api/v2/account_statement/items': new _routeClasses.RoutePost(),
  /** Get the total number of open and closed account statement items for a customer. */
  '/api/v2/account_statement/totals': new _routeClasses.RoutePost()
};
const addressRoutes = {
  /** Validate an address. */
  '/api/v3/address/validate': new _routeClasses.RoutePost()
};
const affiliateRoutes = {
  /** Get stats for an affiliate */
  '/api/v2/affiliate/get_stats': new _routeClasses.RoutePost(),
  /** Get the amount of earned points in each period of the specified range. */
  '/api/v2/affiliate/get_earned_points_by_period': new _routeClasses.RoutePost(),
  /** Get the total balance at the end of each period of the specified range (for periods with activity) */
  '/api/v2/affiliate/get_points_balance_by_period': new _routeClasses.RoutePost(),
  /** Get the number of referrals in each period of the specified range. */
  '/api/v2/affiliate/get_referral_count_by_period': new _routeClasses.RoutePost(),
  /** Get the number of conversions in each period of the specified range. */
  '/api/v2/affiliate/get_conversions_by_period': new _routeClasses.RoutePost(),
  /** Get the amount of cents paid out to an affiliate in each period of the specified range. */
  '/api/v2/affiliate/get_payouts_by_period': new _routeClasses.RoutePost(),
  /**Get an affiliate by URL code */
  '/api/v2/affiliate/get_by_code': new _routeClasses.RoutePost(),
  /** Update the allowed fields for an affiliate. */
  '/api/v2/affiliate/update': new _routeClasses.RoutePost(),
  /** Manually redeem points for cash or credit. */
  '/api/v2/affiliate/redeem_points': new _routeClasses.RoutePost(),
  /** Gets all affiliates. */
  '/api/v3/affiliate/all': new _routeClasses.RoutePostForUseModel({
    model: 'affiliate'
  }),
  /** Create a new affiliate for a customer. */
  '/api/v3/affiliate/create': new _routeClasses.RoutePostForUseMutator(),
  /** Gets an affiliate by ID. */
  '/api/v3/affiliate/get': new _routeClasses.RoutePostForUseModel({
    model: 'affiliate',
    filter: (affiliates, params) => affiliates.filter(a => a.id === params.id),
    tryFilter: true
  }),
  /** Searched for affiliates. */
  '/api/v3/affiliate/paginated_search': new _routeClasses.RoutePost(),
  /** Gets several affiliates by their IDs. */
  '/api/v3/affiliate/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'affiliate',
    filter: (affiliates, params) => affiliates.filter(a => params.ids.includes(a.id)),
    key: 'ids'
  }),
  /** Gets affiliates with the given customer ID. */
  '/api/v3/affiliate/get_by_customer_id': new _routeClasses.RoutePostForUseModel({
    model: 'affiliate',
    filter: (affiliates, params) => affiliates.filter(a => a.customer_id === params.customer_id)
    // tryFilter: true, // There should only ever be one affiliate per customer, so it is safe to use tryFilter.
  }),

  /** Update an affiliate. */
  '/api/v3/affiliate/update': new _routeClasses.RoutePostForUseMutator()
};
const arcifyRoutes = {
  '/api/v2/arcify': new _routeClasses.RoutePostStream(),
  '/api/v2/arcify/getJson': new _routeClasses.RoutePost(),
  '/api/v2/arcify/createDraft': new _routeClasses.RoutePost(),
  '/api/v2/arcify/duplicate': new _routeClasses.RoutePost()
};
const attachmentRoutes = {
  /** Gets an attachment. */
  '/api/v3/attachment/get': new _routeClasses.RoutePostForUseModel({
    model: 'attachment',
    filter: (attachments, params) => attachments.filter(a => a.id === params.id),
    tryFilter: true
  }),
  /** Gets an attachment. */
  '/api/v3/attachment/ids': new _routeClasses.RoutePostForUseModel({
    model: 'attachment',
    filter: (attachments, params) => attachments.filter(a => params.ids.includes(a.id)),
    key: 'ids'
  }),
  /**
   * Gets attachments that match one or more properties. If multiple properties are provided, attachments must match all
   * properties.
   */
  '/api/v3/attachment/for_model': new _routeClasses.RoutePostForUseModel({
    model: 'attachment',
    filter: (attachments, params) => attachments.filter(a => {
      if (a.deleted) return false;
      const union = 'union' in params ? params.union : [params];
      return union.some(u => {
        let key;
        for (key in u) {
          if (u[key] !== a[key]) {
            return false;
          }
        }
        return true;
      });
    })
  }),
  /** Updates an attachment. */
  '/api/v3/attachment/update': new _routeClasses.RoutePostForUseMutator()
};
const bankTransactionRoutes = {
  /**
   * Gets a list of all bank transactions that reference the provided customer ID, but that do not yet reference a
   * transaction item.
   */
  '/api/v3/bank_transaction/get_unassigned_for_customer': new _routeClasses.RoutePost(),
  /**
   * Gets all refundable Stripe payments associated with an invoice.
   */
  '/api/v3/bank_transaction/get_refundable_stripe_payments_for_invoice': new _routeClasses.RoutePost()
};
const bomOperationRoutes = {
  /** Retrieve all bom operations for a given item. */
  '/api/v3/bom_operation/get_for_item': new _routeClasses.RoutePostForUseModel({
    model: 'bom_operation',
    filter: (bomOperations, params) => bomOperations.filter(bomOperation => params.item_id === bomOperation.item_id && (params.operation_ids ? params.operation_ids.includes(bomOperation.operation_id) : true))
  }),
  /** Retrieve all bom operations for the given items. */
  '/api/v3/bom_operation/get_for_items': new _routeClasses.RoutePostForUseModel({
    model: 'bom_operation',
    filter: (bomOperations, params) => bomOperations.filter(bomOperation => params.item_ids.includes(bomOperation.item_id) && (params.operation_ids ? params.operation_ids.includes(bomOperation.operation_id) : true)),
    key: 'item_ids'
  }),
  /** Returns bom operations and bom items for the given item, with nested child boms. */
  '/api/v3/bom_operation/get_for_item_recursive': new _routeClasses.RoutePost()
};
const bomOperationItemRoutes = {
  /** Retrieve all bom operation items for a given bom_operation. */
  '/api/v3/bom_operation_item/get_for_bom_operation': new _routeClasses.RoutePostForUseModel({
    model: 'bom_operation_item',
    filter: (bomOperations, params) => bomOperations.filter(bomOperationItem => params.bom_operation_id === bomOperationItem.bom_operation_id)
  }),
  /** Retrieve all bom operation items for a given set of bom_operations. */
  '/api/v3/bom_operation_item/get_for_bom_operation_ids': new _routeClasses.RoutePostForUseModel({
    model: 'bom_operation_item',
    filter: (bomOperationItems, params) => bomOperationItems.filter(bomOperationItem => params.bom_operation_ids.includes(bomOperationItem.bom_operation_id)),
    key: 'bom_operation_ids'
  })
};
const boxRoutes = {
  /** Gets all boxes. */
  '/api/v3/box/all': new _routeClasses.RoutePostForUseModel({
    model: 'box'
  })
};
const clientLogRoutes = {
  /** Return all client logs. */
  '/api/v2/client_log/all': new _routeClasses.RoutePost(),
  /** Return all open client logs. */
  '/api/v2/client_log/open': new _routeClasses.RoutePost()
};
const companyRoutes = {
  /** Gets a company. */
  '/api/v3/company/get': new _routeClasses.RoutePostForUseModel({
    model: 'company',
    filter: (companies, params) => companies.filter(c => c.id === params.id),
    tryFilter: true
  }),
  /** Updates a company. */
  '/api/v3/company/update': new _routeClasses.RoutePostForUseMutator(),
  /** Creates a company. */
  '/api/v3/company/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes a company. */
  '/api/v3/company/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get all companies. */
  '/api/v3/company/all': new _routeClasses.RoutePostForUseModel({
    model: 'company'
  })
};
const companyExemptFormRoutes = {
  /** Gets a company exempt form. */
  '/api/v3/company_exempt_form/get': new _routeClasses.RoutePostForUseModel({
    model: 'company_exempt_form',
    filter: (companyExemptForms, params) => companyExemptForms.filter(c => c.id === params.id),
    tryFilter: true
  }),
  /** Updates a company exempt form. */
  '/api/v3/company_exempt_form/update': new _routeClasses.RoutePostForUseMutator(),
  /** Creates a company exempt form. */
  '/api/v3/company_exempt_form/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes a company exempt form. */
  '/api/v3/company_exempt_form/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Gets company exempt forms for the given company. */
  '/api/v3/company_exempt_form/for_company': new _routeClasses.RoutePostForUseModel({
    model: 'company_exempt_form',
    filter: (companyExemptForms, params) => companyExemptForms.filter(c => c.company_id === params.company_id && !c.deleted)
  })
};
const couponRoutes = {
  /** Retrieve a coupon */
  '/api/v3/coupon/get': new _routeClasses.RoutePostForUseModel({
    model: 'coupon',
    filter: (coupons, params) => coupons.filter(item => item.id === params.id),
    tryFilter: true
  }),
  /** Retrieve a coupon by code */
  '/api/v3/coupon/get_with_code': new _routeClasses.RoutePostForUseModel({
    model: 'coupon',
    filter: (coupons, params) => coupons.filter(item => item.code === params.code),
    tryFilter: true
  }),
  /** Update a coupon */
  '/api/v3/coupon/update': new _routeClasses.RoutePostForUseMutator(),
  /** Create a coupon */
  '/api/v3/coupon/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get all coupons. */
  '/api/v3/coupon/all': new _routeClasses.RoutePostForUseModel({
    model: 'coupon'
  })
};
const creditNotes = {
  /** Initiate a refund using a credit note. */
  '/api/v3/credit_note/create_stripe_refund': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieve a credit note by ID */
  '/api/v3/credit_note/get': new _routeClasses.RoutePostForUseModel({
    model: 'credit_note',
    filter: (creditNotes, params) => creditNotes.filter(cn => cn.id === params.id),
    tryFilter: true
  }),
  /** Retrieve several credit notes by id. */
  '/api/v3/credit_note/ids': new _routeClasses.RoutePostForUseModel({
    model: 'credit_note',
    filter: (creditNotes, params) => creditNotes.filter(creditNote => params.ids.includes(creditNote.id)),
    key: 'ids'
  }),
  /** Retrieve all credit notes for a given customer */
  '/api/v3/credit_note/get_for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'credit_note',
    filter: (creditNotes, params) => creditNotes.filter(cn => cn.customer_id === params.customer_id)
  }),
  /** Retrieve all credit notes for a given invoice */
  '/api/v3/credit_note/get_for_sales_invoice': new _routeClasses.RoutePostForUseModel({
    model: 'credit_note',
    filter: (creditNotes, params) => creditNotes.filter(cn => cn.sales_invoice_id === params.sales_invoice_id)
  }),
  /** Retrieve the minimum void date for a credit note */
  '/api/v3/credit_note/get_minimum_void_date': new _routeClasses.RoutePost(),
  /** Retrieve all open credit notes for a given customer */
  '/api/v3/credit_note/get_open_for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'credit_note',
    filter: (creditNotes, params) => creditNotes.filter(cn => cn.customer_id === params.customer_id && cn.status === 'approved' && cn.unused_balance_cents > 0)
  }),
  /** Create a credit note */
  '/api/v3/credit_note/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a credit note */
  '/api/v3/credit_note/update': new _routeClasses.RoutePostForUseMutator(),
  /** Approve a credit note */
  '/api/v3/credit_note/approve': new _routeClasses.RoutePostForUseMutator(),
  /** Void a credit note */
  '/api/v3/credit_note/void': new _routeClasses.RoutePostForUseMutator()
};
const customerRoutes = {
  '/api/v1/customer/conversions_attributable_to_period': new _routeClasses.RoutePost(),
  /** Signup for a new customer account. */
  '/api/v2/customer/create': new _routeClasses.RoutePost(),
  /** Gets the customer for the currently logged in customer_user. */
  '/api/v2/customer/get': new _routeClasses.RoutePost(),
  /** Retrieve payment methods for a customer. */
  '/api/v2/customer/payment_methods': new _routeClasses.RoutePost(),
  /** Retrieves all customers. */
  '/api/v3/customer/all': new _routeClasses.RoutePost(),
  /** Retrieve customer by id. */
  '/api/v3/customer/get': new _routeClasses.RoutePostForUseModel({
    model: 'customer',
    filter: (customers, params) => customers.filter(c => c.id === params.id),
    tryFilter: true
  }),
  /** Retrieve customers by list of ids. */
  '/api/v3/customer/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'customer',
    filter: (customers, params) => customers.filter(c => params.ids.includes(c.id))
  }),
  /** Retrieve all customers in a given company. */
  '/api/v3/customer/get_for_company': new _routeClasses.RoutePostForUseModel({
    model: 'customer',
    filter: (customers, params) => customers.filter(c => c.company_id === params.company_id)
  }),
  /** Searches for customers and returns paginated results. */
  '/api/v3/customer/paginated_search': new _routeClasses.RoutePost(),
  /** Update a customer */
  '/api/v3/customer/update': new _routeClasses.RoutePostForUseMutator()
};
const customerAccountLedgerRoutes = {
  /** Retrieve all ledger entries for a given invoice */
  '/api/v3/customer_account_ledger/get_for_invoice': new _routeClasses.RoutePostForUseModel({
    model: 'customer_account_ledger',
    filter: (ledgerEntries, params) => ledgerEntries.filter(l => l.sales_invoice_id === params.id)
  }),
  /** Retrieve all ledger entries for a given credit note */
  '/api/v3/customer_account_ledger/get_for_credit_note': new _routeClasses.RoutePostForUseModel({
    model: 'customer_account_ledger',
    filter: (ledgerEntries, params) => ledgerEntries.filter(l => l.credit_note_id === params.id)
  })
};
const customerUserRoutes = {
  /** Customer endpoint to update a customer user. */
  '/api/v3/customer_user/admin_update': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieve customer user by id. */
  '/api/v3/customer_user/get': new _routeClasses.RoutePostForUseModel({
    model: 'customer_user',
    filter: (customerUsers, params) => customerUsers.filter(c => c.id === params.id),
    tryFilter: true
  }),
  /** Returns the currently authenticated customer_user. */
  '/api/v3/customer_user/get_by_token': new _routeClasses.RoutePost(),
  /** Returns customer users for an authorized customer. */
  '/api/v3/customer_user/for_authorized_customer': new _routeClasses.RoutePost(),
  /** Retrieve all customer users for in a given customer. */
  '/api/v3/customer_user/for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'customer_user',
    filter: (customerUsers, params) => customerUsers.filter(c => c.customer_id === params.customer_id)
  }),
  /** Login as a customer user. */
  '/api/v3/customer_user/login': new _routeClasses.RoutePost(),
  /** Search for customer users and return a paginated result. */
  '/api/v3/customer_user/paginated_search': new _routeClasses.RoutePost(),
  /** Update a customer user */
  '/api/v3/customer_user/update': new _routeClasses.RoutePostForUseMutator(),
  /** Accepts an invitation to join a company. */
  '/api/v3/customer_user/validate_invite': new _routeClasses.RoutePost()
};
const cutConditionRoutes = {
  /** Get all cut conditions */
  '/api/v1/cut_condition/all': new _routeClasses.RoutePost(),
  /** Get all cut conditions */
  '/api/v3/cut_condition/all': new _routeClasses.RoutePostForUseModel({
    model: 'cut_condition'
  }),
  /** Retrieve several cut conditions by id. */
  '/api/v3/cut_condition/ids': new _routeClasses.RoutePostForUseModel({
    model: 'cut_condition',
    filter: (cutConditions, params) => cutConditions.filter(item => params.ids.includes(item.id)),
    key: 'ids'
  })
};
const cutConditionFrequencyRoutes = {
  /** Get all cut condition frequencies */
  '/api/v1/cut_condition_frequency/all': new _routeClasses.RoutePost(),
  /** Get all cut condition frequencies for the given material */
  '/api/v1/cut_condition_frequency/get_for_material': new _routeClasses.RoutePost()
};
const databaseSchemaRoutes = {
  '/api/v3/database/current_schema': new _routeClasses.RoutePost(),
  '/api/v3/database/target_schema': new _routeClasses.RoutePost(),
  '/api/v3/database/diff_schema': new _routeClasses.RoutePost()
};
const earnedRevenueRoutes = {
  /** Retrieve earned revenue in one or more 24-hr periods. */
  '/api/v2/production/get_earned_revenue': new _routeClasses.RoutePostForUseModel({
    model: 'earned_revenue',
    filter: (earnedRevenue, params) => earnedRevenue.filter(item => params.periodStarts.some(p => p === item.startTimestamp)),
    key: 'periodStarts'
  })
};
const eventRoutes = {
  /** Create an event */
  '/api/v3/event/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get events by type, optionally filtering by start and ending timestamp */
  '/api/v3/event/get_by_type': new _routeClasses.RoutePostForUseModel({
    model: 'event',
    filter: (events, params) => events.filter(item => item.type === params.type && (params.start_timestamp == null || item.timestamp >= params.start_timestamp) && (params.end_timestamp == null || item.timestamp <= params.end_timestamp))
  })
};
const facilityRoutes = {
  /** Get all facilities. */
  '/api/v3/facility/all': new _routeClasses.RoutePostForUseModel({
    model: 'facility'
  }),
  /** Create a new facility */
  '/api/v3/facility/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a facility. */
  '/api/v3/facility/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get a facility by id */
  '/api/v3/facility/get': new _routeClasses.RoutePostForUseModel({
    model: 'facility',
    filter: (facility, params) => facility.filter(f => f.id === params.id),
    tryFilter: true
  }),
  /** Get a facility by ip address. */
  '/api/v3/facility/get_for_ip': new _routeClasses.RoutePost(),
  /** Update a facility */
  '/api/v3/facility/update': new _routeClasses.RoutePostForUseMutator()
};
const globalRoutes = {
  /**
   * Perform fuzzy search for orders. First tries to match internal_id, and if there is no match, performs a fuzzy
   * search on name, email, company, phone, etc. `scope` is so that you can separtely perform a quick search on recent
   * orders, and a slower search on older orders. 
   */
  '/api/v2/global/fuzzy_search': new _routeClasses.RoutePost()
};
const hardwareRoutes = {
  "/api/v2/hardware/all": new _routeClasses.RoutePost(),
  "/api/v2/hardware/get": new _routeClasses.RoutePost(),
  "/api/v3/hardware/all": new _routeClasses.RoutePostForUseModel({
    model: "hardware"
  }),
  "/api/v3/hardware/get": new _routeClasses.RoutePostForUseModel({
    model: "hardware",
    filter: (hardware, params) => hardware.filter(h => h.id === params.id),
    tryFilter: true
  }),
  "/api/v3/hardware/update": new _routeClasses.RoutePostForUseMutator(),
  "/api/v3/hardware/create": new _routeClasses.RoutePostForUseMutator()
};
const imagekitRoutes = {
  /** Get the authentication tokens for the imagekit API. */
  '/api/v3/imagekit_auth': new _routeClasses.RoutePost()
};
const itemInventoryRoutes = {
  /** Retrieves all inventory */
  '/api/v3/item_inventory/all': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: itemInventory => itemInventory.filter(a => a.qty > 0)
  }),
  /** Adjusts the properties of an item inventory. */
  '/api/v3/item_inventory/adjust': new _routeClasses.RoutePostForUseMutator(),
  /**
   * Creates a new item_inventory entry.  
   * Some notes:
   * - `material_sheet_id` or `material_tube_id` must match that of `item_id`
   * - This endpoint will throw an error if `facility_id` is not provided, but your IP does not have an associated facility
   * - Both `size_x` and `size_y` must be provided for items of type `metal_sheet`
   * - Only `size_x`, not `size_y`, can be provided for items of type `metal_tube`
   * - Size, qty, original qty and cost must all be non-negative.
   */
  '/api/v3/item_inventory/create': new _routeClasses.RoutePostForUseMutator(),
  /** Gets all inventory for an item */
  '/api/v3/item_inventory/for_item': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => a.item_id === params.item_id && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Retrieves all item inventory for a given set of acc_item IDs */
  '/api/v3/item_inventory/for_items': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => params.item_ids.includes(a.item_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Retrieves all item inventory for po items */
  '/api/v3/item_inventory/for_po_items': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => params.po_item_ids.includes(a.purchase_order_item_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Get item inventory for a barcode */
  '/api/v3/item_inventory/for_specific_id_barcode': new _routeClasses.RoutePost(),
  /** Get item inventory for a location barcode */
  '/api/v3/item_inventory/for_specific_id_location_barcode': new _routeClasses.RoutePost(),
  /** Retrieves all item inventory for a given material_sheet_id */
  '/api/v3/item_inventory/for_sheet': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => a.material_sheet_id === params.material_sheet_id && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Retrieves all item inventory for a given set of  material_sheet_ids */
  '/api/v3/item_inventory/for_sheets': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => params.material_sheet_ids.includes(a.material_sheet_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Retrieves all item inventory for a given material_tube_id */
  '/api/v3/item_inventory/for_tube': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => a.material_tube_id === params.material_tube_id && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Retrieves all item inventory for a given set of material_tube_ids */
  '/api/v3/item_inventory/for_tubes': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => params.material_tube_ids.includes(a.material_tube_id) && (0, _inventoryFilters.itemInventoryFilter)(a, params))
  }),
  /** Returns an object map of item inventory, with tasked information, for the given material sheet and process, in the given facility. */
  '/api/v3/item_inventory/get_for_sheet_or_process': new _routeClasses.RoutePost(),
  /** Allocates and returns a new item inventory barcode. */
  '/api/v3/item_inventory/get_new_barcode': new _routeClasses.RoutePost(),
  /** Retrieves several item inventory objects by id */
  '/api/v3/item_inventory/ids': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory',
    filter: (itemInventory, params) => itemInventory.filter(a => params.ids.includes(a.id)),
    key: 'ids'
  }),
  '/api/v3/item_inventory/get_sheet_deficits': new _routeClasses.RoutePost(),
  '/api/v3/item_inventory/get_sheet_usage_by_month': new _routeClasses.RoutePost(),
  /** Retrieve the image data for the specified material study sheet ids. */
  '/api/v3/item_inventory/get_study_sheet_images': new _routeClasses.RoutePost(),
  /** Retrieves item inventory in a paginated search. */
  '/api/v3/item_inventory/paginated_search': new _routeClasses.RoutePost(),
  /** Clears the location_barcode of an item inventory. */
  '/api/v3/item_inventory/pull': new _routeClasses.RoutePostForUseMutator(),
  /** Receive an item_inventory against a PO. */
  '/api/v3/item_inventory/receive': new _routeClasses.RoutePostForUseMutator(),
  /** Scraps items in inventory. */
  '/api/v3/item_inventory/scrap': new _routeClasses.RoutePostForUseMutator(),
  /** Splits a sheet of material into smaller sheets, or splits a stack into smaller stacks. */
  '/api/v3/item_inventory/split_sheet': new _routeClasses.RoutePostForUseMutator(),
  /** Splits a tube of material into smaller tubes, or splits a stack into smaller stacks. */
  '/api/v3/item_inventory/split_tube': new _routeClasses.RoutePostForUseMutator(),
  /** Store an item inventory in a location */
  '/api/v3/item_inventory/store': new _routeClasses.RoutePostForUseMutator(),
  /** Transfers FIFO item inventory to a new location. */
  '/api/v3/item_inventory/transfer_fifo': new _routeClasses.RoutePostForUseMutator(),
  /** Transfers specific-ID item inventory to a new location. */
  '/api/v3/item_inventory/transfer_specific': new _routeClasses.RoutePostForUseMutator(),
  /** Unreceives an item_inventory entry, only if none of the qty has been used */
  '/api/v3/item_inventory/unreceive': new _routeClasses.RoutePostForUseMutator()
};
const itemInventoryLocationRoutes = {
  /** Adjusts the qty of FIFO inventory in the item_inventory_location given by id */
  '/api/v3/item_inventory_location/adjust': new _routeClasses.RoutePostForUseMutator(),
  /** Adjusts the floating qty of a FIFO item to reach the target qty. */
  '/api/v3/item_inventory_location/adjust_floating_qty': new _routeClasses.RoutePostForUseMutator(),
  /** Consumes FIFO inventory in the item_inventory_location given by id */
  '/api/v3/item_inventory_location/consume': new _routeClasses.RoutePostForUseMutator(),
  /** Get item_inventory_locations for an item. */
  '/api/v3/item_inventory_location/for_item': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory_location',
    filter: (itemInventoryLocations, params) => itemInventoryLocations.filter(a => params.item_id === a.item_id && (params.facility_id === undefined || a.facility_id === params.facility_id))
  }),
  /** Get item_inventory_locations for a list of items. */
  '/api/v3/item_inventory_location/for_items': new _routeClasses.RoutePostForUseModel({
    model: 'item_inventory_location',
    filter: (itemInventoryLocations, params) => itemInventoryLocations.filter(a => params.item_ids.includes(a.item_id) && (params.facility_id === undefined || a.facility_id === params.facility_id))
  }),
  /** Get item inventory locations matching a barcode */
  '/api/v3/item_inventory_location/for_location_barcode': new _routeClasses.RoutePost(),
  /** Removes the item_inventory_location entry given by id */
  '/api/v3/item_inventory_location/pull': new _routeClasses.RoutePostForUseMutator(),
  /** Adds a qty of the given item to a location. */
  '/api/v3/item_inventory_location/store': new _routeClasses.RoutePostForUseMutator()
};

/** @deprecated */
const labelRoutes = {
  /** Get several labels by id */
  '/api/v3/label/ids': new _routeClasses.RoutePostForUseModel({
    model: 'label',
    filter: (labels, params) => labels.filter(l => params.ids.includes(l.id)),
    key: 'ids'
  })
};

/** @deprecated */
const labelPartRoutes = {
  /** Get all label parts for a list of labels */
  '/api/v3/label_part/get_for_labels': new _routeClasses.RoutePostForUseModel({
    model: 'label_part',
    filter: (labelParts, params) => labelParts.filter(part => params.label_ids.includes(part.label_id)),
    key: 'label_ids'
  })
};
const leadinPresetsRoutes = {
  /** Get lead in presets for the given material sheet id. */
  '/api/v3/leadin_presets/get_for_material_sheet_id': new _routeClasses.RoutePostForUseModel({
    model: 'leadin_presets',
    filter: (leadinPresets, params) => leadinPresets.filter(preset => preset.material_sheet_id === params.material_sheet_id)
  }),
  /** Create or update an existing leadin preset. */
  '/api/v3/leadin_presets/save': new _routeClasses.RoutePostForUseMutator()
};
const libraryPartRoutes = {
  '/api/v2/library_part/check_part_number_availability': new _routeClasses.RoutePost(),
  /** Clears the part number for an item, removing it from the part library. */
  '/api/v2/library_part/clear_part_number': new _routeClasses.RoutePost(),
  /** Gets all library parts for an order. */
  '/api/v2/library_part/for_order': new _routeClasses.RoutePost(),
  /** Gets a single library part by id. */
  '/api/v2/library_part/get': new _routeClasses.RoutePost(),
  /** Retrieve library parts for a customer by part numbers. */
  '/api/v2/library_part/get_by_customer_part_numbers': new _routeClasses.RoutePost(),
  /** Gets all unique tags for the current customer's library parts. */
  '/api/v2/library_part/get_distinct_tags': new _routeClasses.RoutePost(),
  /** Retrieve several library parts by id. */
  '/api/v2/library_part/ids': new _routeClasses.RoutePost(),
  /** Search a customer's part library, and return paginated results. */
  '/api/v2/library_part/paginated_search': new _routeClasses.RoutePost(),
  /** Update the customer_archived flag of several library parts. */
  '/api/v2/library_part/update_customer_archived': new _routeClasses.RoutePost(),
  /** Update the tags of a library part. */
  '/api/v2/library_part/update_tags': new _routeClasses.RoutePost(),
  /** Set or update a part number for a library part */
  '/api/v2/library_part/upsert_part_number': new _routeClasses.RoutePost()
};
const logRoutes = {
  '/api/v1/log/statistics': new _routeClasses.RoutePost()
};
const machineRoutes = {
  /** Retrieve all machines. */
  '/api/v3/machine/all': new _routeClasses.RoutePostForUseModel({
    model: 'machine'
  }),
  /** Create a new machine. */
  '/api/v3/machine/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update an existing machine. */
  '/api/v3/machine/update': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a machine. */
  '/api/v3/machine/delete': new _routeClasses.RoutePostForUseMutator()
};
const machineStateAggregateRoutes = {
  /** Gets a range of machine state aggregates by machine id and period duration, between a start and end timestamp */
  '/api/v3/machine_state_aggregate/get_range': new _routeClasses.RoutePostForUseModel({
    model: 'machine_state_aggregate',
    filter: (aggregates, params) => aggregates.filter(aggregate => aggregate.machine_id === params.machine_id && aggregate.period_start >= params.start && aggregate.period_start < params.end && aggregate.period_duration === params.period_duration)
  })
};
const materialRoutes = {
  /** Retrieve all material types joined with material sheets. */
  '/api/v1/materials': new _routeClasses.RoutePost(),
  /**
   * Retrieve customer-allowed properties of all material types, sheets, and tubes, in a single request. The response is
   * an object with three properties (the types, sheets, and tubes) which must be "unpacked" using `fromTupleMaterials`.
   */
  '/api/v2/materials': new _routeClasses.RoutePost()
};
const materialSheetRoutes = {
  /** Retrieve all material sheets. */
  '/api/v3/material_sheet/all': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet'
  }),
  /** Retrieve a single material sheet.*/
  '/api/v3/material_sheet/get': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet',
    filter: (materialSheets, params) => materialSheets.filter(m => m.id === params.id),
    tryFilter: true
  }),
  /** Retrieve a set of material sheets by id. */
  '/api/v3/material_sheet/ids': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet',
    filter: (materialSheets, params) => materialSheets.filter(m => params.ids.some(id => m.id === id))
  })
};
const materialSheetConditionRoutes = {
  /** Retrieve a single material sheet condition by id. */
  '/api/v3/material_sheet_condition/get': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet_condition',
    filter: (materialSheetConditions, params) => materialSheetConditions.filter(m => m.id === params.id),
    tryFilter: true
  }),
  /** Retrieve all material sheet conditions, even deleted ones. */
  '/api/v3/material_sheet_condition/all': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet_condition'
  }),
  /** Retrieve material sheet conditions for a single material sheet. */
  '/api/v3/material_sheet_condition/for_material_sheet': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet_condition',
    filter: (materialSheetConditions, params) => materialSheetConditions.filter(m => m.material_sheet_id === params.material_sheet_id)
  }),
  /** Retrieve material sheet conditions for a single cut condition. */
  '/api/v3/material_sheet_condition/for_cut_condition': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet_condition',
    filter: (materialSheetConditions, params) => materialSheetConditions.filter(m => m.cut_condition_id === params.cut_condition_id)
  }),
  /** Retrieve material sheet conditions for an array of cut conditions. */
  '/api/v3/material_sheet_condition/for_cut_conditions': new _routeClasses.RoutePostForUseModel({
    model: 'material_sheet_condition',
    filter: (materialSheetConditions, params) => materialSheetConditions.filter(m => params.cut_condition_ids.some(id => m.cut_condition_id === id)),
    key: 'cut_condition_ids'
  }),
  /** Create an association between a material_sheet and a cut_condition by creating a new material_sheet_condition. */
  '/api/v3/material_sheet_condition/create': new _routeClasses.RoutePostForUseMutator(),
  /** 
   * Remove an association between a material_sheet and a cut_condition by the id of the existing
   * material_sheet_condition. The `deleted` flag of the material sheet condition is set, but the actual object is not
   * removed from the database.
   */
  '/api/v3/material_sheet_condition/delete': new _routeClasses.RoutePostForUseMutator()
};
const materialTubeRoutes = {
  /** Retrieve all material tubes. */
  '/api/v3/material_tube/all': new _routeClasses.RoutePostForUseModel({
    model: 'material_tube'
  }),
  /** Retrieve a single material tube. */
  '/api/v3/material_tube/get': new _routeClasses.RoutePostForUseModel({
    model: 'material_tube',
    filter: (materialTubes, params) => materialTubes.filter(m => m.id === params.id),
    tryFilter: true
  }),
  /** Create a new material tube. */
  '/api/v3/material_tube/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update an existing material tube. */
  '/api/v3/material_tube/update': new _routeClasses.RoutePostForUseMutator()
};
const materialTypeRoutes = {
  /** Retrieve all material types. */
  '/api/v3/material/all': new _routeClasses.RoutePostForUseModel({
    model: 'material_type'
  }),
  /** Retrieve a single material type */
  '/api/v3/material/get': new _routeClasses.RoutePostForUseModel({
    model: 'material_type',
    filter: (materials, params) => materials.filter(m => m.material_id === params.material_id),
    tryFilter: true
  })
};
const messageRoutes = {
  /** Get customer messages. */
  '/api/v2/messages': new _routeClasses.RoutePost()
};
const moveEventDocumentRoutes = {
  /** Add items to a move event document */
  '/api/v3/move_event_document/add_items': new _routeClasses.RoutePostForUseMutator(),
  /** Creates a new move event document */
  '/api/v3/move_event_document/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes a move event document */
  '/api/v3/move_event_document/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Finishes a move event document. */
  '/api/v3/move_event_document/finish': new _routeClasses.RoutePostForUseMutator(),
  /** Gets a move event document by ID */
  '/api/v3/move_event_document/get': new _routeClasses.RoutePostForUseModel({
    model: 'move_event_document',
    filter: (moveEventDocuments, params) => moveEventDocuments.filter(m => m.id === params.id),
    tryFilter: true
  }),
  /** Gets all move event documents for the given work order items of a given status. */
  '/api/v3/move_event_document/get_for_work_order_items': new _routeClasses.RoutePostForUseModel({
    model: 'move_event_document',
    filter: (moveEventDocuments, params) => {
      // Handle each case individually so that future additions to the status enum are noticed.
      let statuses;
      switch (params.status) {
        case 'any':
          statuses = ['draft', 'finished', 'undone'];
          break;
        case 'closed':
          statuses = ['finished', 'undone'];
          break;
        case 'draft':
          statuses = ['draft'];
          break;
        case 'finished':
          statuses = ['finished'];
          break;
        case 'undone':
          statuses = ['undone'];
          break;
        default:
          let _exhaustiveCheck = params.status;
          throw new Error(`Unhandled status: ${_exhaustiveCheck}`);
      }
      return moveEventDocuments.filter(m => {
        if (!statuses.includes(m.status)) {
          return false;
        }
        if (params.src_operation_id != null && m.src_operation_id !== params.src_operation_id) {
          return false;
        }
        // TODO: This might be too slow
        if (!m.items.some(i => params.work_order_item_ids.includes(i.work_order_item_id))) {
          return false;
        }
        return true;
      });
    },
    key: 'work_order_item_ids'
  }),
  /** Gets several move event documents by ID */
  '/api/v3/move_event_document/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'move_event_document',
    filter: (moveEventDocuments, params) => moveEventDocuments.filter(m => params.ids.includes(m.id)),
    key: 'ids'
  }),
  /** Gets all open move event documents for a given source operation */
  '/api/v3/move_event_document/get_open_for_operation': new _routeClasses.RoutePostForUseModel({
    model: 'move_event_document',
    filter: (moveEventDocuments, params) => moveEventDocuments.filter(m => m.src_operation_id === params.src_operation_id && m.status === 'draft')
  }),
  /** Merges two move event documents */
  '/api/v3/move_event_document/merge': new _routeClasses.RoutePostForUseMutator(),
  /** Remove items from a move event document */
  '/api/v3/move_event_document/remove_items': new _routeClasses.RoutePostForUseMutator(),
  /** Stops all timers for the given move event. */
  '/api/v3/move_event_document/stop_all_timers': new _routeClasses.RoutePostForUseMutator(),
  /** Undoes a move event document. */
  '/api/v3/move_event_document/undo': new _routeClasses.RoutePostForUseMutator(),
  /** Updates a move event document */
  '/api/v3/move_event_document/update': new _routeClasses.RoutePostForUseMutator(),
  /** Update cost items in a move event document */
  '/api/v3/move_event_document/update_cost_items': new _routeClasses.RoutePostForUseMutator(),
  /** Update items in a move event document */
  '/api/v3/move_event_document/update_items': new _routeClasses.RoutePostForUseMutator()
};
const nestRoutes = {
  /** Perform a nesting operation, or cancel an existing job. */
  '/api/v2/nest': new _routeClasses.RoutePostStream(),
  /** Perform an admin nesting operation. */
  '/api/v3/nest': new _routeClasses.RoutePostStream()
};
const nestingCacheRoutes = {
  '/api/v3/nesting_cache/all': new _routeClasses.RoutePost()
};
const nestPartRoutes = {
  /** Retrieve all nest parts for a given process. */
  '/api/v1/nest_part/for_process': new _routeClasses.RoutePost(),
  '/api/v1/nest_part/get_json_program': new _routeClasses.RoutePost(),
  /** Retrieve all nest parts for a given process. */
  '/api/v3/nest_part/for_process': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part',
    filter: (nestParts, params) => nestParts.filter(nestPart => nestPart.process_id === params.process_id)
  }),
  /** Retrieve all nest parts for a given process. */
  '/api/v3/nest_part/for_processes': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part',
    filter: (nestParts, params) => nestParts.filter(nestPart => params.process_ids.includes(nestPart.process_id)),
    key: 'process_ids'
  }),
  /** Retrieve a nest part by ID. */
  '/api/v3/nest_part/get': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part',
    filter: (nestParts, params) => nestParts.filter(nestPart => nestPart.id === params.id),
    tryFilter: true
  }),
  /** Retrieve a set of nest parts by ID */
  '/api/v3/nest_part/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part',
    filter: (nestParts, params) => nestParts.filter(nestPart => params.ids.includes(nestPart.id)),
    key: 'ids'
  }),
  /** Retrieve all nest parts */
  '/api/v3/nest_part/all': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part'
  }),
  /** Retrieve all nest parts for the given work order items */
  '/api/v3/nest_part/for_work_order_items': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part',
    filter: (nestParts, params) => nestParts.filter(nestPart => params.work_order_item_ids.includes(nestPart.work_order_item_id)),
    key: 'work_order_item_ids'
  })
};
const nestPartPoseRoutes = {
  /** Retrieve all nest part pose for a given nest sheet. */
  '/api/v1/nest_part_pose/for_nest_sheet': new _routeClasses.RoutePost(),
  /** Retrieve all nest part pose for a given list of nest sheets. */
  '/api/v3/nest_part_pose/for_nest_sheets': new _routeClasses.RoutePostForUseModel({
    model: 'nest_part_pose',
    filter: (nestPartPose, params) => nestPartPose.filter(npp => params.nest_sheet_ids.includes(npp.nest_sheet_id)),
    key: 'nest_sheet_ids'
  }),
  /**
   * Retrieve all nest part pose for a given process. (Note that a filter is not possible so more nest part pose may
   * be returned to than just these)
   */
  '/api/v1/nest_part_pose/for_process': new _routeClasses.RoutePost()
};
const nestSheetRoutes = {
  /** Retrieve all nest sheets for a given process. */
  '/api/v1/nest_sheet/for_process': new _routeClasses.RoutePost(),
  /** Retrieve all nest sheets for the given processes. */
  '/api/v3/nest_sheet/for_processes': new _routeClasses.RoutePostForUseModel({
    model: 'nest_sheet',
    filter: (nestSheets, params) => nestSheets.filter(ns => params.process_ids.includes(ns.process_id)),
    key: 'process_ids'
  }),
  /** Retrieve a single nest sheet by its id or uuid. */
  '/api/v1/nest_sheet/get': new _routeClasses.RoutePost(),
  /** Retrieve nest sheets by IDs */
  '/api/v1/nest_sheet/get_ids': new _routeClasses.RoutePost(),
  /** Retrieve nest sheets by IDs */
  '/api/v3/nest_sheet/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'nest_sheet',
    filter: (nestSheets, params) => nestSheets.filter(ns => params.ids.includes(ns.id))
  }),
  /** Update a nestSheet. */
  '/api/v1/nest_sheet/update': new _routeClasses.RoutePost()
};
const nestSheetDenestRoutes = {
  /** Get nest_sheet_denest objects still awaiting denest */
  '/api/v3/nest_sheet_denest/awaiting_denest': new _routeClasses.RoutePostForUseModel({
    model: 'nest_sheet_denest',
    filter: nestSheetDenest => nestSheetDenest.filter(ns => ns.status === 'awaiting_denest')
  }),
  /** Retrieve a single denest sheet. */
  '/api/v3/nest_sheet_denest/get': new _routeClasses.RoutePostForUseModel({
    model: 'nest_sheet_denest',
    filter: (nestSheetDenest, params) => nestSheetDenest.filter(ns => ns.id === params.id),
    tryFilter: true
  }),
  /** Gets all nest sheet denests that match the provided filters. */
  '/api/v3/nest_sheet_denest/get_filtered': new _routeClasses.RoutePostForUseModel({
    model: 'nest_sheet_denest',
    filter: (nestSheetDenest, params) => nestSheetDenest.filter(ns => {
      var _ns$cut_finish_timest;
      if (params.machine_id != null && ns.machine_id !== params.machine_id) return false;
      if (params.deck_name != null && ns.deck_name !== params.deck_name) return false;
      if (params.min_finish_timestamp != null && ((_ns$cut_finish_timest = ns.cut_finish_timestamp) !== null && _ns$cut_finish_timest !== void 0 ? _ns$cut_finish_timest : Infinity) < params.min_finish_timestamp) return false;
      let statuses = params.statuses || ['pending', 'cutting', 'awaiting_denest'];
      if (!statuses.includes(ns.status)) return false;
      return true;
    })
  }),
  /** Move all nest_sheet_denest objects from one deck to another deck */
  '/api/v3/nest_sheet_denest/move_all_to_deck': new _routeClasses.RoutePostForUseMutator(),
  /**
   * Marks a nest_sheet_denest as cutting, its nest_sheet as in_progress, and its process as in_progress. Any other
   * nest_sheet_denests which are currently cutting on the same machine will be marked as "pending", and their parent
   * objects' status will be updated accordingly.
   */
  '/api/v3/nest_sheet_denest/start_cutting': new _routeClasses.RoutePostForUseMutator(),
  /**
   * Marks a nest_sheet_denest as pending, its nest_sheet as in_progress, and its process as in_progress. (If the parent
   * nest_sheet and process have other child objects which are still cutting, they won't be updated.)
   */
  '/api/v3/nest_sheet_denest/stop_cutting': new _routeClasses.RoutePostForUseMutator(),
  /** Update a nest_sheet_denest object */
  '/api/v3/nest_sheet_denest/update': new _routeClasses.RoutePostForUseMutator(),
  /** Finish denesting a sheet */
  '/api/v3/nest_sheet_denest/finish': new _routeClasses.RoutePostForUseMutator(),
  /**
   * Finish cutting a sheet. This marks the nest_sheet_denest as "awaiting_denest". It also increments the qty_remaining
   * field of the parent nest_sheet, and marks the nest_sheet and the process as finished if all sheets have been cut.
   */
  '/api/v3/nest_sheet_denest/finish_cutting': new _routeClasses.RoutePostForUseMutator(),
  /**
   * Undoes cutting a sheet. This marks the nest_sheet_denest as "pending". It also decrements the qty_remaining field
   * of the parent nest_sheet, and marks the nest_sheet as "in_progress" or "pending", and the process as "in_progress"
   * or released, depending on whether any other sheets have been cut.
   */
  '/api/v3/nest_sheet_denest/undo_finish_cutting': new _routeClasses.RoutePostForUseMutator()
};
const notesRoutes = {
  /** Get all notes */
  '/api/v3/note/all': new _routeClasses.RoutePostForUseModel({
    model: 'note'
  }),
  /** Create a new note */
  '/api/v3/note/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update an existing note */
  '/api/v3/note/update': new _routeClasses.RoutePostForUseMutator(),
  /** Get a note by ID */
  '/api/v3/note/get': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => n.id === params.id),
    tryFilter: true
  }),
  /** Get notes for a customer */
  '/api/v3/note/for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => n.customer_id === params.customer_id)
  }),
  /** Get notes for an order */
  '/api/v3/note/for_order': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => n.order_id === params.order_id)
  }),
  /** Get notes for a part */
  '/api/v3/note/for_part': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => n.part_id === params.part_id)
  }),
  /** Get notes associated with a provided set of bom operation ids. */
  '/api/v3/note/for_bom_operations': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => params.bom_op_ids.includes(n.bom_operation_id)),
    key: 'bom_op_ids'
  }),
  /** Get notes for a model for a given set of ids of the model . */
  '/api/v3/note/for_model_by_model_ids': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => {
      if (!params.includeSystemNotes && n.user_id === 0) return false;
      const modelId = n[params.model];
      return params.ids.includes(modelId);
    })
  }),
  /** Get notes matching a set of parameters. If multiple parameters are provided, notes must match all of them. */
  '/api/v3/note/for_models': new _routeClasses.RoutePostForUseModel({
    model: 'note',
    filter: (notes, params) => notes.filter(n => {
      if (n.deleted) return false;
      const union = 'union' in params ? params.union : [params];
      return union.some(u => {
        let key;
        for (key in u) {
          if (u[key] !== n[key]) {
            return false;
          }
        }
        return true;
      });
    })
  })
};
const nppDenestRoutes = {
  /** Get npp_denest objects for a specified denest_sheet_id */
  '/api/v3/npp_denest/for_denest_sheet': new _routeClasses.RoutePostForUseModel({
    model: 'npp_denest',
    filter: (nppDenest, params) => nppDenest.filter(npp => npp.nest_sheet_denest_id === params.denest_sheet_id)
  }),
  /** Update npp denest for a denest sheet */
  '/api/v3/npp_denest/group_update': new _routeClasses.RoutePostForUseMutator()
};
const operationRoutes = {
  /** Retrieve all operations */
  '/api/v3/operation/all': new _routeClasses.RoutePostForUseModel({
    model: 'operation'
  }),
  /** Create an operation. */
  '/api/v3/operation/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update an operation. */
  '/api/v3/operation/update': new _routeClasses.RoutePostForUseMutator(),
  /** Delete an operation. */
  '/api/v3/operation/delete': new _routeClasses.RoutePostForUseMutator()
};
const orderRoutes = {
  /** Return the total tube revenue in the previous 30 days. */
  '/api/v1/order/tube_revenue': new _routeClasses.RoutePost(),
  /** Set an order's customer_id to the currently logged in customer. */
  '/api/v2/order/claim': new _routeClasses.RoutePost(),
  /** Create a draft order. */
  '/api/v2/order/create': new _routeClasses.RoutePost(),
  /** Searches for orders and parts and returns a paginated result. */
  '/api/v2/order/for_customer_paginated': new _routeClasses.RoutePost(),
  /** Gets an order. */
  '/api/v2/order/get': new _routeClasses.RoutePost(),
  /** Gets several orders by id. */
  '/api/v2/order/get_ids': new _routeClasses.RoutePost(),
  /** Obtain shipping quotes for the given order from each enabled carrier. */
  '/api/v2/order/ship/quote': new _routeClasses.RoutePost(),
  /** Submits an order. */
  '/api/v2/order/submit': new _routeClasses.RoutePostStream(),
  /** Updates an order. */
  '/api/v2/order/update': new _routeClasses.RoutePost(),
  /** Run order automation on the given order, charging the order and creating a shipping order if ready. */
  '/api/v3/order/charge_and_create_shipping_order_if_ready': new _routeClasses.RoutePost(),
  /** Retrieve several orders by id. */
  '/api/v3/order/ids': new _routeClasses.RoutePostForUseModel({
    model: 'order',
    filter: (orders, params) => orders.filter(o => params.ids.includes(o.id)),
    key: 'ids'
  }),
  /** Retrieve orders by their internal_ids */
  '/api/v3/order/internal_ids': new _routeClasses.RoutePostForUseModel({
    model: 'order',
    filter: (orders, params) => orders.filter(o => params.ids.includes(o.internal_id)),
    key: 'ids'
  }),
  /** Retrieve orders using a filter. */
  '/api/v1/order/get': new _routeClasses.RoutePost(),
  /** Retrieve orders using a filter. */
  '/api/v3/order/get': new _routeClasses.RoutePostForUseModel({
    model: 'order',
    filter: (orders, params) => orders.filter(order => {
      if (params.any_in_process && ['processing', 'hold_material', 'hold_payment', 'hold_feedback', 'hold_review'].includes(order.status)) return true;
      if (order.status === 'draft' || order.status === 'deleted' || order.status === 'saved') return false;
      if (params.customer_id != null && params.customer_id > 0 && order.customer_id !== params.customer_id) return false;
      if (params.startDate != null && order.order_date < params.startDate) return false;
      if (params.endDate != null && order.order_date > params.endDate) return false;
      if (params.status != null && !params.status.includes(order.status)) return false;
      return true;
    })
  }),
  /** Retrieve submitted orders by customer id. */
  '/api/v3/order/for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'order',
    filter: (orders, params) => orders.filter(o => {
      var _params$excludeStatus;
      if (o.customer_id !== params.customer_id) return false;
      if (params.includeStatuses) {
        return params.includeStatuses.includes(o.status);
      }
      let excludeStatuses = (_params$excludeStatus = params.excludeStatuses) !== null && _params$excludeStatus !== void 0 ? _params$excludeStatus : ['draft', 'deleted', 'saved'];
      return !excludeStatuses.includes(o.status);
    })
  }),
  /** Mark an order as shipped */
  '/api/v3/order/mark_shipped': new _routeClasses.RoutePostForUseMutator(),
  /** Resubmits an order, updating the totals, order parts, and order cost details. Creates a transaction and a note. */
  '/api/v3/order/resubmit': new _routeClasses.RoutePost(),
  /** Quote an order. */
  '/api/v3/order/quote': new _routeClasses.RoutePost(),
  /** Update the payment method on an order */
  '/api/v2/order/update_payment_method': new _routeClasses.RoutePost(),
  /** Update the payment method on an order */

  '/api/v2/order/quote/get': new _routeClasses.RoutePost(),
  '/api/v2/order/quote/place_order': new _routeClasses.RoutePost(),
  '/api/v2/order/quote/cancel': new _routeClasses.RoutePost(),
  '/api/v2/order/quote/update': new _routeClasses.RoutePost(),
  '/api/v2/order/quote/is_quote_expired': new _routeClasses.RoutePost(),
  '/api/v2/order/quote/requote': new _routeClasses.RoutePost(),
  '/api/v3/order/quote/place_order': new _routeClasses.RoutePostForUseMutator(),
  '/api/v3/order/quote/refresh': new _routeClasses.RoutePostForUseMutator()
};
const orderCalendarRoutes = {
  /** Retrieve several order calendar statuses by ID */
  '/api/v3/order_calendar/ids': new _routeClasses.RoutePostForUseModel({
    model: 'order_calendar',
    filter: (orderCalendar, params) => orderCalendar.filter(oc => params.order_ids.includes(oc.id)),
    key: 'order_ids'
  }),
  /** Search for order calendar statuses by filters */
  '/api/v3/order_calendar': new _routeClasses.RoutePostForUseModel({
    model: 'order_calendar',
    filter: (orderCalendar, params) => orderCalendar.filter(oc => {
      var _params$include_unshi;
      const statusIsNotDraftDeletedSaved = oc.status !== 'draft' && oc.status !== 'deleted' && oc.status !== 'saved';
      const targetShipDateInRange = oc.target_ship_date >= params.start_timestamp && oc.target_ship_date <= params.end_timestamp;
      let returnValue = statusIsNotDraftDeletedSaved && targetShipDateInRange;
      if (Array.isArray(params.statuses) && params.statuses.length > 0) {
        const statusIsIncluded = params.statuses.includes(oc.status);
        returnValue && (returnValue = statusIsIncluded);
      }
      const includeUnshipped = (_params$include_unshi = params.include_unshipped) !== null && _params$include_unshi !== void 0 ? _params$include_unshi : true;
      if (includeUnshipped) {
        const statusIsUnshipped = ['processing', 'hold_material', 'hold_payment', 'hold_feedback', 'hold_review', 'hold_pickup'].includes(oc.status);
        returnValue || (returnValue = statusIsUnshipped);
      }
      return returnValue;
    })
  })
};
const orderCostDetailRoutes = {
  /** Retrieve a single OrderCostDetail by id. */
  '/api/v3/order_cost_detail/get': new _routeClasses.RoutePostForUseModel({
    model: 'order_cost_detail',
    filter: (orderCostDetails, params) => orderCostDetails.filter(ocd => ocd.id === params.id),
    tryFilter: true
  }),
  /** Create an OrderCostDetail. */
  '/api/v3/order_cost_detail/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update an OrderCostDetail. */
  '/api/v3/order_cost_detail/update': new _routeClasses.RoutePostForUseMutator(),
  /** Delete an OrderCostDetail. */
  '/api/v3/order_cost_detail/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieves order_cost_details in a given order. */
  '/api/v3/order_cost_detail/for_order': new _routeClasses.RoutePostForUseModel({
    model: 'order_cost_detail',
    filter: (orderCostDetails, params) => orderCostDetails.filter(ocd => ocd.order_id === params.order_id)
  }),
  /** Replaces all order cost details in an order with new ones. */
  '/api/v3/order_cost_detail/replace_in_order': new _routeClasses.RoutePostForUseMutator()
};
const orderPartRoutes = {
  /** Retrieve statistics about the versions of arcify parts currently in production. */
  '/api/v1/order_part/get_versions_in_production': new _routeClasses.RoutePost(),
  /** Adds an order part to an order, or increases the qty of an existing order part. */
  '/api/v2/order_part/add_to_order': new _routeClasses.RoutePost(),
  /** Deletes multiple order parts. */
  '/api/v2/order_part/deleteMultiple': new _routeClasses.RoutePost(),
  /** Gets all order parts in an order. */
  '/api/v2/order_part/for_order': new _routeClasses.RoutePost(),
  '/api/v2/order_part/update': new _routeClasses.RoutePost(),
  /** Retrieves order_parts. */
  '/api/v3/order_part/all': new _routeClasses.RoutePostForUseModel({
    model: 'order_part'
  }),
  /** Creates a new order part */
  '/api/v3/order_part/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes an order part */
  '/api/v3/order_part/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieves order_parts in a given set of orders by order ids */
  '/api/v3/order_part/for_orders': new _routeClasses.RoutePostForUseModel({
    model: 'order_part',
    filter: (orderParts, params) => orderParts.filter(op => op.order_id != null && params.order_ids.includes(op.order_id)),
    key: 'order_ids'
  }),
  /** Retrieves order_parts for an array of item ids and order ids. */
  '/api/v3/order_part/get_for_items_and_orders': new _routeClasses.RoutePostForUseModel({
    model: 'order_part',
    filter: (orderParts, params) => orderParts.filter(op => params.item_ids.includes(op.item_id) && params.order_ids.includes(op.order_id))
  }),
  /** Retrieves order_parts for an array of part ids. */
  '/api/v3/order_part/get_for_part_ids': new _routeClasses.RoutePostForUseModel({
    model: 'order_part',
    filter: (orderParts, params) => orderParts.filter(op => params.part_ids.includes(op.part_id)),
    key: 'part_ids'
  }),
  /** Retrieve several order parts by id. */
  '/api/v3/order_part/ids': new _routeClasses.RoutePostForUseModel({
    model: 'order_part',
    filter: (orderParts, params) => orderParts.filter(op => params.ids.includes(op.id)),
    key: 'ids'
  }),
  /** Retrieves order_parts in a given order. */
  '/api/v3/order_part/in_order': new _routeClasses.RoutePostForUseModel({
    model: 'order_part',
    filter: (orderParts, params) => orderParts.filter(op => op.order_id === params.order_id)
  }),
  /** Update an order part */
  '/api/v3/order_part/update': new _routeClasses.RoutePostForUseMutator()
};
const partRoutes = {
  /** Search for a part by the internal id of its order part */
  '/api/v1/part/search_by_internal_id': new _routeClasses.RoutePost(),
  /** Search for a part by its order part id */
  '/api/v1/part/search_by_order_part_id': new _routeClasses.RoutePost(),
  /** Search for a part by its id */
  '/api/v1/part/search_by_part_id': new _routeClasses.RoutePost(),
  /** Retrieve statistics about part uploads. */
  '/api/v1/part/upload_errors_by_period': new _routeClasses.RoutePost(),
  /** Gets all parts for an order. */
  '/api/v2/part/for_order': new _routeClasses.RoutePost(),
  /** Retrieve several parts by id */
  '/api/v3/part/ids': new _routeClasses.RoutePostForUseModel({
    model: 'part',
    filter: (parts, params) => parts.filter(p => params.ids.includes(p.id)),
    key: 'ids'
  }),
  '/api/v3/part/quote': new _routeClasses.RoutePost()
};
const partProgramRoutes = {
  /** Retrieve several part programs by id. */
  '/api/v1/part_program/get_ids': new _routeClasses.RoutePost(),
  /** Retrieve several part programs by id. */
  '/api/v3/part_program/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'part_program',
    filter: (partPrograms, params) => partPrograms.filter(pp => params.ids.includes(pp.id)),
    key: 'ids'
  }),
  /** Retrieve compatible part programs for the given part */
  '/api/v1/part_program/get_compatible_programs_for_part': new _routeClasses.RoutePost()
};
const paymentMethodRoutes = {
  /** Create a setup intent with stripe */
  '/api/v2/payment_method/setup_intent': new _routeClasses.RoutePost(),
  /** Create a setup intent with stripe for a specific invoice */
  '/api/v2/payment_method/setup_intent_for_invoice': new _routeClasses.RoutePost(),
  /** Create a setup intent with stripe for a quoted order */
  '/api/v2/payment_method/setup_intent_for_quote': new _routeClasses.RoutePost(),
  /**
   * Create a new Customer Payment Method
   * This may also cause updates to existing payment methods
   * */
  '/api/v2/payment_method/create': new _routeClasses.RoutePost(),
  /** Mark a payment method as deleted */
  '/api/v2/payment_method/delete': new _routeClasses.RoutePost(),
  /** Get a single payment method by guid */
  '/api/v2/payment_method/get': new _routeClasses.RoutePost(),
  /** Get the primary or backup payment method of the logged in customer
   * Will return null if the customer does not have a payment method set as primary or backup */
  '/api/v2/payment_method/get_default': new _routeClasses.RoutePost(),
  /** Get list of current payment methods */
  '/api/v2/payment_method/list': new _routeClasses.RoutePost(),
  /**
   * Update a payment method.
   * This may also cause updates to existing payment methods
   * */
  '/api/v2/payment_method/update': new _routeClasses.RoutePost(),
  /** Get the micro deposit type for an ach_bank_debit payment method which needs verification */
  '/api/v2/payment_method/get_bank_account_microdeposit_type': new _routeClasses.RoutePost(),
  /** Verify the bank account for a given payment method */
  '/api/v2/payment_method/verify_bank_account': new _routeClasses.RoutePost(),
  /**
   * ------------------
   * V3 Admin endpoints
   * ------------------
   * */

  /** Creates a new customer payment method */
  '/api/v3/payment_method/create_for_customer': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single payment method by guid */
  '/api/v3/payment_method/get': new _routeClasses.RoutePostForUseModel({
    model: 'customer_payment_method',
    filter: (paymentMethods, params) => paymentMethods.filter(pm => pm.guid === params.guid),
    tryFilter: true
  }),
  /** Get the primary or backup payment method for the customer user.
   * Will return null if the customer user does not have a payment method set as primary or backup */
  '/api/v3/payment_method/get_default_for_customer_user': new _routeClasses.RoutePost(),
  /** Get all current payment methods for a given customer */
  '/api/v3/payment_method/get_for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'customer_payment_method',
    filter: (paymentMethods, params) => paymentMethods.filter(pm => pm.customer_id === params.customer_id)
  }),
  /** Create a draft setup intent on behalf of the customer */
  '/api/v3/payment_method/setup_intent_for_customer': new _routeClasses.RoutePost()
};
const paymentRoutes = {
  /** Paginated query for customer payments  */
  '/api/v2/payments/paginated': new _routeClasses.RoutePost()
};
const permissionGroupRoutes = {
  /** Retrieve all permission groups. */
  '/api/v3/permission_group/all': new _routeClasses.RoutePostForUseModel({
    model: 'permission_group'
  }),
  /** Create a permission group. */
  '/api/v3/permission_group/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a permission group. */
  '/api/v3/permission_group/update': new _routeClasses.RoutePostForUseMutator()
};
const pipeStatusRoutes = {
  /** Returns pipe status for each order part in the given order */
  '/api/v2/pipe_status/for_order_parts_of_order': new _routeClasses.RoutePost(),
  /** Returns pipe status for each order part in the given orders */
  '/api/v2/pipe_status/for_order_parts_of_orders': new _routeClasses.RoutePost(),
  /**
    * Retrieves the pipe status of all processing order parts and, optionally, that include a given pipeline step with
    * any quantity. The result can be further filtered by specifying a range of steps, one of which must contain a
    * non-zero quantity of parts.
    */
  '/api/v3/pipe_status/get_filtered': new _routeClasses.RoutePostForUseModel({
    model: 'pipe_status',
    filter: (statuses, params) => statuses.filter(status => {
      var _params$startOperatio, _params$endOperationI;
      // NOTE: See similar function in oshcut_rest/Models/pipe.js, get_filtered
      if (params.hasOperation) {
        // Part must contain the given step
        let indexStep = status.status.findIndex(s => s.operation === params.hasOperation);
        if (indexStep === -1) {
          return false;
        }
      }
      if (params.hasDeficit) {
        // Part must have a deficit in its cut facility
        if (status.deficit <= 0) {
          return false;
        }
        if (params.facilityId && status.cutFacilityId !== params.facilityId) {
          return false;
        }
      }
      let stepMin = (_params$startOperatio = params.startOperationInclusive) !== null && _params$startOperatio !== void 0 ? _params$startOperatio : params.startOperationExclusive;
      let indexMin = stepMin ? status.status.findIndex(s => s.operation === stepMin) : 0;
      if (indexMin === -1) {
        // Part must contain the lower bound as one of its steps, otherwise it can't be an endpoint of the
        // range
        return false;
      }
      let stepMax = (_params$endOperationI = params.endOperationInclusive) !== null && _params$endOperationI !== void 0 ? _params$endOperationI : params.endOperationExclusive;
      let indexMax = stepMax ? status.status.findIndex(s => s.operation === stepMax) : status.status.length - 1;
      if (indexMax === -1) {
        // Part must contain the upper bound as one of its steps, otherwise it can't be an endpoint of the
        // range
        return false;
      }
      if (stepMin !== null && stepMin !== void 0 ? stepMin : stepMax) {
        // At least one step in the range must contain a non-zero quantity
        for (let i = indexMin; i <= indexMax; i++) {
          if (params.startOperationExclusive && !params.startOperationInclusive && i === indexMin) {
            // Skip the lower bound if it's exclusive
            continue;
          }
          if (params.endOperationExclusive && !params.endOperationInclusive && i === indexMax) {
            // Skip the upper bound if it's exclusive
            continue;
          }
          if (status.status[i].operation === 'SCRAP') {
            continue;
          }
          if (!params.facilityId && status.status[i].qty > 0) {
            return true;
          }
          if (params.facilityId && status.status[i].qtyByFacility[params.facilityId] > 0) {
            return true;
          }
        }

        // No step in the range contained a non-zero quantity
        return false;
      }

      // No filters returned false, so the part passes
      return true;
    })
  }),
  /** Retrieve the pipe status of a work order item by id. */
  '/api/v3/pipe_status/get_for_work_order_item': new _routeClasses.RoutePostForUseModel({
    model: 'pipe_status',
    filter: (pipeStatus, params) => pipeStatus.filter(ps => params.work_order_item_id === ps.work_order_item_id),
    tryFilter: true
  }),
  /** Retrieve the pipe status of several work order items by id. */
  '/api/v3/pipe_status/get_for_work_order_items': new _routeClasses.RoutePostForUseModel({
    model: 'pipe_status',
    filter: (pipeStatus, params) => pipeStatus.filter(ps => params.work_order_item_ids.includes(ps.work_order_item_id)),
    key: 'work_order_item_ids'
  }),
  /** Gets the next pipeline operation for the given work order item. */
  '/api/v3/pipe_status/next_step': new _routeClasses.RoutePost(),
  /** Gets the previous pipeline operation for the given work order item. */
  '/api/v3/pipe_status/previous_step': new _routeClasses.RoutePost(),
  /**
   * Retrieve the pipe status of several order parts by id.
   * @deprecated Use `/api/v3/pipe_status/get_for_work_order_items` instead.
   */
  '/api/v3/pipe_status/get_for_order_parts': new _routeClasses.RoutePostForUseModel({
    model: 'pipe_status',
    filter: (pipeStatus, params) => pipeStatus.filter(ps => params.order_part_ids.includes(ps.id)),
    key: 'order_part_ids'
  }),
  /** Moves an order part through the pipe. */
  '/api/v3/pipe_status/move': new _routeClasses.RoutePostForUseMutator()
};
const powderRoutes = {
  /** Retrieve all powders */
  '/api/v3/powder/all': new _routeClasses.RoutePostForUseModel({
    model: 'powder'
  })
};
const powderJobRoutes = {
  /** Retrieve a single powder job. */
  '/api/v3/powder_job/get': new _routeClasses.RoutePostForUseModel({
    model: 'powder_job',
    filter: (powderJobs, params) => powderJobs.filter(job => job.id === params.id),
    tryFilter: true
  }),
  /** Retrieves recent powder jobs. */
  '/api/v3/powder_job/recent': new _routeClasses.RoutePostForUseModel({
    model: 'powder_job'
  }),
  /** Retrieve several powder jobs by id. */
  '/api/v3/powder_job/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'powder_job',
    filter: (powderJobs, params) => powderJobs.filter(job => params.ids.includes(job.id)),
    key: 'ids'
  }),
  /** Create a new powder job with the given parameters. */
  '/api/v3/powder_job/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a powder job. */
  '/api/v3/powder_job/update': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieve powder jobs for a given order. */
  '/api/v3/powder_job/for_order': new _routeClasses.RoutePostForUseModel({
    model: 'powder_job'
  }),
  /** Retrieve powder jobs for several orders. */
  '/api/v3/powder_job/for_orders': new _routeClasses.RoutePostForUseModel({
    model: 'powder_job'
  }),
  /** Delete a powder job. */
  '/api/v3/powder_job/delete': new _routeClasses.RoutePostForUseMutator()
};
const powderPartRoutes = {
  /** Retrieve a single powder part. */
  '/api/v3/powder_part/get': new _routeClasses.RoutePostForUseModel({
    model: 'powder_part',
    filter: (powderParts, params) => powderParts.filter(part => part.id === params.id),
    tryFilter: true
  }),
  /** Retrieve several powder parts by id. */
  '/api/v3/powder_part/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'powder_part',
    filter: (powderParts, params) => powderParts.filter(part => params.ids.includes(part.id)),
    key: 'ids'
  }),
  /** Create a new powder part with the given parameters. */
  '/api/v3/powder_part/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a powder part. */
  '/api/v3/powder_part/update': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a powder part. */
  '/api/v3/powder_part/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieve powder parts for a given powder job. */
  '/api/v3/powder_part/for_powder_job': new _routeClasses.RoutePostForUseModel({
    model: 'powder_part',
    filter: (powderParts, params) => powderParts.filter(part => part.powder_job_id === params.powder_job_id)
  }),
  /** Retrieve powder parts for several powder jobs. */
  '/api/v3/powder_part/for_powder_jobs': new _routeClasses.RoutePostForUseModel({
    model: 'powder_part',
    filter: (powderParts, params) => powderParts.filter(part => params.powder_job_ids.includes(part.powder_job_id)),
    key: 'powder_job_ids'
  })
};
const processRoutes = {
  '/api/v1/process/get_with_status': new _routeClasses.RoutePost(),
  /** Retrieve a single process. */
  '/api/v1/process/get': new _routeClasses.RoutePost(),
  /** Retrieve a single process. */
  '/api/v3/process/get': new _routeClasses.RoutePostForUseModel({
    model: 'process',
    filter: (processes, params) => processes.filter(process => process.id === params.id),
    tryFilter: true
  }),
  /** Retrieve multiple processes. */
  '/api/v3/process/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'process',
    filter: (processes, params) => processes.filter(process => params.ids.includes(process.id)),
    key: 'ids'
  }),
  /** Create a new process with the given parameters. */
  '/api/v3/process/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a process. */
  '/api/v1/process/update': new _routeClasses.RoutePost(),
  /** Retrieve processes corresponding to nest_sheet IDs */
  '/api/v3/process/for_nest_sheets': new _routeClasses.RoutePost(),
  /** Retrieve processes that match the supplied status(es). */
  '/api/v3/process/get_with_status': new _routeClasses.RoutePostForUseModel({
    model: 'process',
    filter: (processes, params) => processes.filter(p => params.statuses.includes(p.status))
  }),
  /** Gets released processes, optionally filtered by machine. */
  '/api/v3/process/working': new _routeClasses.RoutePostForUseModel({
    model: 'process',
    filter: (processes, params) => {
      const statuses = ['released', 'in_progress', 'hold', 'exception'];
      return processes.filter(p => statuses.includes(p.status) && (params.machine_id ? p.machine_id === params.machine_id : true));
    }
  }),
  '/api/v3/process/release': new _routeClasses.RoutePost(),
  /** Gets processes finished within the last 24 hours, or since from_date if provided, optionally filtered by machine. */
  '/api/v3/process/finished': new _routeClasses.RoutePostForUseModel({
    model: 'process',
    filter: (processes, params) => {
      const from_date = params.from_date || Date.now() - 1000 * 60 * 60 * 24;
      return processes.filter(p => p.status === 'finished' && (params.machine_id ? p.machine_id === params.machine_id : true) && p.finish_date && p.finish_date >= from_date);
    }
  })
};
const productionRoutes = {
  /** Get the total cost of support orders in the given period. */
  '/api/v2/production/get_support_order_costs': new _routeClasses.RoutePost()
};
const qualityRoutes = {
  /** Finish a part in the quality console */
  '/api/v3/quality/finish': new _routeClasses.RoutePostForUseMutator(),
  /** Adjust the finished qty of a part in the quality console */
  '/api/v3/quality/adjust_finished_qty': new _routeClasses.RoutePostForUseMutator(),
  /** Get a list of inventory locations for finished goods in the given work order */
  '/api/v3/quality/finished_inventory_locations': new _routeClasses.RoutePost()
};
const remDenestRoutes = {
  /** Get rem_denest objects for a specified denest_sheet_id */
  '/api/v3/rem_denest/for_denest_sheet': new _routeClasses.RoutePostForUseModel({
    model: 'rem_denest',
    filter: (remDenest, params) => remDenest.filter(rem => rem.denest_sheet_id === params.denest_sheet_id)
  }),
  /**
  * Update a rem_denest objects using the denest_sheet_id and the descriptor string.
  * If a rem_denest object does not exist matching that denest_sheet_id and descriptor, a 
  * new one is created
  */
  '/api/v3/rem_denest/update': new _routeClasses.RoutePostForUseMutator(),
  /** Delete several rem_denest objects by id. */
  '/api/v3/rem_denest/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get a unique barcode for the next remainder */
  '/api/v3/rem_denest/get_new_barcode': new _routeClasses.RoutePost()
};
const resourceLocks = {
  /** Acquire a lock */
  '/api/v3/resource_lock/acquire': new _routeClasses.RoutePostForUseMutator(),
  /** Renew a lock */
  '/api/v3/resource_lock/renew': new _routeClasses.RoutePostForUseMutator(),
  /** Release a lock */
  '/api/v3/resource_lock/release': new _routeClasses.RoutePostForUseMutator(),
  /** Get all unexpired resource locks */
  '/api/v3/resource_lock/all': new _routeClasses.RoutePostForUseModel({
    model: 'resource_lock',
    filter: resourceLocks => resourceLocks.filter(resourceLock => resourceLock.expires_timestamp > Date.now() - 5000) // Include some buffer time
  })
};

const salesInvoiceRoutes = {
  /** Get basic info for a sales invoice, with no PII. */
  '/api/v2/sales_invoice/get_basic_info': new _routeClasses.RoutePost(),
  /** Get sales invoices for an order. */
  '/api/v2/sales_invoice/get_for_order': new _routeClasses.RoutePost(),
  /** Pay one or more invoices. */
  '/api/v2/sales_invoice/pay': new _routeClasses.RoutePost(),
  /** Pay an invoice. */
  '/api/v2/sales_invoice/pay_single': new _routeClasses.RoutePost(),
  /** Approve an invoice */
  '/api/v3/sales_invoice/approve': new _routeClasses.RoutePostForUseMutator(),
  /** Create a new sales invoice */
  '/api/v3/sales_invoice/create': new _routeClasses.RoutePostForUseMutator(),
  /** Permanently deletes a sales_invoice */
  '/api/v3/sales_invoice/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieve a sales invoice */
  '/api/v3/sales_invoice/get': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice',
    filter: (salesInvoices, params) => salesInvoices.filter(si => si.id === params.id),
    tryFilter: true
  }),
  /** Retrieve all sales invoices for a given customer */
  '/api/v3/sales_invoice/get_for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice',
    filter: (salesInvoices, params) => salesInvoices.filter(si => si.customer_id === params.customer_id)
  }),
  /** Retrieve sales invoices for a given sales order */
  '/api/v3/sales_invoice/get_for_sales_order': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice',
    filter: (salesInvoices, params) => salesInvoices.filter(si => si.sales_order_id === params.sales_order_id)
  }),
  /** Get the ids of the matching invoices in paginated results. */
  '/api/v3/sales_invoice/get_ids_paginated': new _routeClasses.RoutePost(),
  /** Retrieve the earliest allowed void date for a sales invoice. */
  '/api/v3/sales_invoice/get_minimum_void_date': new _routeClasses.RoutePost(),
  /** Retrieve all open sales invoices for a given customer */
  '/api/v3/sales_invoice/get_open_for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice',
    filter: (salesInvoices, params) => salesInvoices.filter(si => si.customer_id === params.customer_id && si.status === 'approved')
  }),
  /** Retrieve several sales invoices by id. */
  '/api/v3/sales_invoice/ids': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice',
    filter: (salesInvoices, params) => salesInvoices.filter(salesInvoice => params.ids.includes(salesInvoice.id)),
    key: 'ids'
  }),
  /** Mark an invoice uncollectible */
  '/api/v3/sales_invoice/mark_uncollectible': new _routeClasses.RoutePostForUseMutator(),
  /** Email sales invoice pdf */
  '/api/v3/sales_invoice/send_invoice_email': new _routeClasses.RoutePost(),
  /** Update a sales invoice */
  '/api/v3/sales_invoice/update': new _routeClasses.RoutePostForUseMutator(),
  /** Void an invoice */
  '/api/v3/sales_invoice/void': new _routeClasses.RoutePostForUseMutator()
};
const salesInvoiceItemRoutes = {
  /** Retrieve a sales invoice item by id */
  '/api/v3/sales_invoice_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice_item',
    filter: (salesInvoiceItem, params) => salesInvoiceItem.filter(si => si.id === params.id),
    tryFilter: true
  }),
  /** Retrieve a list sales invoice items by ids */
  '/api/v3/sales_invoice_item/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice_item',
    filter: (salesInvoiceItems, params) => salesInvoiceItems.filter(sii => params.ids.includes(sii.id)),
    tryFilter: true
  }),
  /** Retrieve a list of sales invoice items by a set of sales invoice ids */
  '/api/v3/sales_invoice_item/get_for_sales_invoices': new _routeClasses.RoutePostForUseModel({
    model: 'sales_invoice_item',
    filter: (salesInvoiceItems, params) => salesInvoiceItems.filter(sii => params.sales_invoice_ids.includes(sii.sales_invoice_id))
  })
};
const scrapRatesRoutes = {
  /** Return process scrap rates in the specified periods */
  '/api/v3/scrap_rates': new _routeClasses.RoutePost()
};
const scrapRecordRoutes = {
  /** Get scrap records for a work order item. (Not sure if this one is useModel compatible.) */
  '/api/v3/scrap_record/get_for_work_order_item': new _routeClasses.RoutePost()
};
const settingsRoutes = {
  /** Get all settings */
  '/api/v3/settings/all': new _routeClasses.RoutePostForUseModel({
    model: 'settings'
  }),
  /** Creates a new setting */
  '/api/v3/settings/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single setting */
  '/api/v3/settings/get': new _routeClasses.RoutePostForUseModel({
    model: 'settings',
    filter: (settings, params) => settings.filter(setting => setting.name === params.name),
    tryFilter: true
  }),
  /** Update a setting */
  '/api/v3/settings/update': new _routeClasses.RoutePostForUseMutator()
};
const shipmentRoutes = {
  /** Create and purchase a new shipment. */
  '/api/v1/shipment/buy': new _routeClasses.RoutePost(),
  '/api/v1/shipment/manual_add': new _routeClasses.RoutePost(),
  /** Obtain shipping quotes from each supported carrier for a parcel. */
  '/api/v1/shipment/quote': new _routeClasses.RoutePost(),
  /** Get shipments for an order. */
  '/api/v2/shipment/get_for_order': new _routeClasses.RoutePost(),
  /** Creates a new shipment  */
  '/api/v3/shipment/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete an existing shipment */
  '/api/v3/shipment/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Cancel an existing shipment */
  '/api/v3/shipment/cancel': new _routeClasses.RoutePostForUseMutator(),
  /** Purchases a shipment which has been previously quoted. */
  '/api/v3/shipment/existing_buy': new _routeClasses.RoutePostForUseMutator(),
  /** Updates the dimensions or weight of a shipment. */
  '/api/v3/shipment/update': new _routeClasses.RoutePostForUseMutator(),
  /** Update a shipment and mark it as shipped, moving all parts in that shipment through the pipeline to DONE. */
  '/api/v3/shipment/update_and_ship': new _routeClasses.RoutePostForUseMutator(),
  /** Gets shipments for several orders. */
  '/api/v3/shipment/get_for_orders': new _routeClasses.RoutePostForUseModel({
    model: 'shipment',
    filter: (shipments, params) => shipments.filter(s => s.order_id != null && params.order_ids.includes(s.order_id)),
    key: 'order_ids'
  }),
  /** Gets shipments for several shipping orders. */
  '/api/v3/shipment/get_for_shipping_orders': new _routeClasses.RoutePostForUseModel({
    model: 'shipment',
    filter: (shipments, params) => shipments.filter(s => s.shipping_order_id != null && params.shipping_order_ids.includes(s.shipping_order_id)),
    key: 'shipping_order_ids'
  }),
  /** Get all shipments within a given date range. */
  '/api/v3/shipment/recent': new _routeClasses.RoutePostForUseModel({
    model: 'shipment',
    filter: (shipments, params) => shipments.filter(s => s.date >= params.startDate && s.date <= params.endDate)
  })
};
const shipmentItemRoutes = {
  /** Gets a shipment item by id. */
  '/api/v3/shipment_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'shipment_item',
    filter: (shipmentItems, params) => shipmentItems.filter(si => si.id === params.id),
    tryFilter: true
  }),
  /** Gets several shipment items by id. */
  '/api/v3/shipment_item/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'shipment_item',
    filter: (shipmentItems, params) => shipmentItems.filter(si => params.ids.includes(si.id)),
    key: 'ids'
  }),
  /** Gets shipment items for a shipment. */
  '/api/v3/shipment_item/get_for_shipment': new _routeClasses.RoutePostForUseModel({
    model: 'shipment_item',
    filter: (shipmentItems, params) => shipmentItems.filter(si => si.shipment_id === params.shipment_id)
  }),
  /** Gets shipment items for several shipments. */
  '/api/v3/shipment_item/get_for_shipments': new _routeClasses.RoutePostForUseModel({
    model: 'shipment_item',
    filter: (shipmentItems, params) => shipmentItems.filter(si => params.shipment_ids.includes(si.shipment_id)),
    key: 'shipment_ids'
  }),
  /** Creates a shipment item. */
  '/api/v3/shipment_item/create': new _routeClasses.RoutePostForUseMutator(),
  /** Updates a shipment item. */
  '/api/v3/shipment_item/update': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes a shipment item. */
  '/api/v3/shipment_item/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes several shipment items. */
  '/api/v3/shipment_item/delete_multiple': new _routeClasses.RoutePostForUseMutator()
};

/** @deprecated */
const shipmentLabelRoutes = {
  /** Retrieves all shipment labels for the given shipment id. */
  '/api/v3/shipment_label/get_for_shipment': new _routeClasses.RoutePostForUseModel({
    model: 'shipment_label',
    filter: (shipmentLabels, params) => shipmentLabels.filter(sl => sl.shipment_id === params.shipment_id)
  })
};
const shippingOrderRoutes = {
  /** Creates a shipping order based on a sales order. */
  '/api/v3/shipping_order/create_for_sales_order': new _routeClasses.RoutePostForUseMutator(),
  /** Gets a shipping order by id. */
  '/api/v3/shipping_order/get': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order',
    filter: (shippingOrders, params) => shippingOrders.filter(so => so.id === params.id),
    tryFilter: true
  }),
  /** Gets several shipping orders by id. */
  '/api/v3/shipping_order/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order',
    filter: (shippingOrders, params) => shippingOrders.filter(so => params.ids.includes(so.id)),
    key: 'ids'
  }),
  /** Finds shipping order matching the given filters. */
  '/api/v3/shipping_order/find': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order',
    filter: (shippingOrders, params) => shippingOrders.filter(so => {
      if (params.statuses != null && params.statuses.length > 0 && !params.statuses.includes(so.status)) return false;
      if (params.primary_sales_order_id != null && so.primary_sales_order_id !== params.primary_sales_order_id) return false;
      if (!!params.is_freight && !(so.ship_carrier.toLowerCase().includes('freight') || so.ship_method.toLowerCase().includes('freight') || so.ship_name.toLowerCase().includes('freight'))) return false;
      if (!!params.is_will_call && !(so.ship_carrier.toLowerCase().includes('pickup') || so.ship_method.toLowerCase().includes('pickup') || so.ship_name.toLowerCase().includes('pickup'))) return false;
      if (!!params.is_parcel && !(!so.ship_carrier.toLowerCase().includes('freight') && !so.ship_method.toLowerCase().includes('freight') && !so.ship_name.toLowerCase().includes('freight') && !so.ship_carrier.toLowerCase().includes('pickup') && !so.ship_method.toLowerCase().includes('pickup') && !so.ship_name.toLowerCase().includes('pickup'))) return false;
      if (params.delivery_location != null && !(so.ship_company.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_name.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_address.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_address2.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_address3.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_city.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_state.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_zip.toLowerCase().includes(params.delivery_location.toLowerCase()) || so.ship_country.toLowerCase().includes(params.delivery_location.toLowerCase()))) return false;

      // if(!!params.is_ready) return false

      // if(!!params.is_will_call_needs_boxing) return false

      if (params.ship_carrier != null && so.ship_carrier !== params.ship_carrier) return false;
      if (params.ship_method != null && so.ship_method !== params.ship_method) return false;
      if (params.customer_id != null && so.customer_id !== params.customer_id) return false;
      if (!!params.is_transfer && !so.is_transfer) return false;
      if (!!params.is_rush && !so.is_rush) return false;
      if (params.facility_id != null && so.src_facility_id !== params.facility_id && so.dst_facility_id !== params.facility_id) return false;
      if (params.src_facility_id != null && so.src_facility_id !== params.src_facility_id) return false;
      if (params.dst_facility_id != null && so.dst_facility_id !== params.dst_facility_id) return false;
      return true;
    })
  }),
  /** Marks a shipping order as ready for pickup. */
  '/api/v3/shipping_order/mark_ready_for_pickup': new _routeClasses.RoutePostForUseMutator(),
  /** Marks a shipping order as finished. */
  '/api/v3/shipping_order/mark_finished': new _routeClasses.RoutePostForUseMutator(),
  /** Updates a shipping order. */
  '/api/v3/shipping_order/update': new _routeClasses.RoutePostForUseMutator(),
  /** Updates the status of a shipping order. */
  '/api/v3/shipping_order/update_status': new _routeClasses.RoutePostForUseMutator()
};
const shippingOrderItemRoutes = {
  /** Creates a shipping order item. */
  '/api/v3/shipping_order_item/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes a shipping order item. */
  '/api/v3/shipping_order_item/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Gets a shipping order item by id. */
  '/api/v3/shipping_order_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order_item',
    filter: (shippingOrderItems, params) => shippingOrderItems.filter(soi => soi.id === params.id),
    tryFilter: true
  }),
  /** Gets several shipping order items by id. */
  '/api/v3/shipping_order_item/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order_item',
    filter: (shippingOrderItems, params) => shippingOrderItems.filter(soi => params.ids.includes(soi.id)),
    key: 'ids'
  }),
  /** Gets shipping order items for a shipping order. */
  '/api/v3/shipping_order_item/get_for_shipping_order': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order_item',
    filter: (shippingOrderItems, params) => shippingOrderItems.filter(soi => soi.shipping_order_id === params.shipping_order_id)
  }),
  /** Gets shipping order items for several shipping orders. */
  '/api/v3/shipping_order_item/get_for_shipping_orders': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order_item',
    filter: (shippingOrderItems, params) => shippingOrderItems.filter(soi => params.shipping_order_ids.includes(soi.shipping_order_id)),
    key: 'shipping_order_ids'
  }),
  /** Gets shipping order items for a sales order. */
  '/api/v3/shipping_order_item/get_for_sales_order': new _routeClasses.RoutePostForUseModel({
    model: 'shipping_order_item',
    filter: (shippingOrderItems, params) => shippingOrderItems.filter(soi => soi.sales_order_id === params.sales_order_id)
  }),
  /** Updates a shipping order item. */
  '/api/v3/shipping_order_item/update': new _routeClasses.RoutePostForUseMutator()
};
const syncCounterRoutes = {
  /** Retrieve the sync counter. */
  '/api/v2/get_sync_counter': new _routeClasses.RoutePostForUseModel({
    model: 'sync_counter'
  })
};
const tapsRoutes = {
  /** Retrieve all taps. */
  '/api/v3/taps/all': new _routeClasses.RoutePostForUseModel({
    model: 'taps'
  })
};
const taskRoutes = {
  /** Assign a user to a task. */
  '/api/v3/task/assign_user': new _routeClasses.RoutePostForUseMutator(),
  /** Create a task. */
  '/api/v3/task/create': new _routeClasses.RoutePostForUseMutator(),
  /** Deletes a task. */
  '/api/v3/task/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Gets finished tasks and returns a paginated result. */
  '/api/v3/task/get_finished_paginated': new _routeClasses.RoutePost(),
  /** Get unfinished tasks for a given user. */
  '/api/v3/task/get_unfinished_for_user': new _routeClasses.RoutePostForUseModel({
    model: 'task',
    filter: (tasks, params) => tasks.filter(t => !t.completed && t.assignees.find(a => a.user_id === params.user_id))
  }),
  /** Get unfinished tasks for a given model. */
  '/api/v3/task/get_unfinished_for_model': new _routeClasses.RoutePostForUseModel({
    model: 'task',
    filter: (tasks, params) => tasks.filter(t => !t.completed && t.links.find(l => l.model === params.model && l.id === params.model_id))
  }),
  /** Get several tasks by id. */
  '/api/v3/task/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'task',
    filter: (tasks, params) => tasks.filter(t => params.ids.includes(t.id)),
    key: 'ids'
  }),
  /** Link a task to a model. */
  '/api/v3/task/link_to_model': new _routeClasses.RoutePostForUseMutator(),
  /** Mark a task as read for a user. */
  '/api/v3/task/mark_as_read': new _routeClasses.RoutePostForUseMutator(),
  /** Mark a task as unread for a user. */
  '/api/v3/task/mark_as_unread': new _routeClasses.RoutePostForUseMutator(),
  /** Snooze a task for a user until a given timestamp. Set snooze_until to null to unsnooze. */
  '/api/v3/task/snooze': new _routeClasses.RoutePostForUseMutator(),
  /** Update task metadata. */
  '/api/v3/task/update': new _routeClasses.RoutePostForUseMutator(),
  /** Unassign a user from a task. */
  '/api/v3/task/unassign_user': new _routeClasses.RoutePostForUseMutator(),
  /** Unlink a task from a model. */
  '/api/v3/task/unlink_from_model': new _routeClasses.RoutePostForUseMutator()
};
const timeTrackingRoutes = {
  /** Gets all timers, finished or not, for a given shipping order */
  '/api/v3/time_tracking/get_all_for_shipping_order': new _routeClasses.RoutePostForUseModel({
    model: 'time_tracking',
    filter: (timers, params) => timers.filter(t => t.shipping_order_id === params.shipping_order_id)
  }),
  /** Gets all running timers for a given user, ideally this should only ever be 1 */
  '/api/v3/time_tracking/get_all_running_for_user': new _routeClasses.RoutePostForUseModel({
    model: 'time_tracking',
    filter: (timers, params) => timers.filter(t => t.user_id === params.user_id && !t.end_timestamp)
  }),
  /** Gets all running timers for a given set of shipping orders */
  '/api/v3/time_tracking/get_all_running_for_shipping_orders': new _routeClasses.RoutePostForUseModel({
    model: 'time_tracking',
    filter: (timers, params) => timers.filter(t => t.end_timestamp === null && params.shipping_order_ids.includes(t.shipping_order_id))
  }),
  /** Start a timer for a given user and task */
  '/api/v3/time_tracking/start': new _routeClasses.RoutePostForUseMutator(),
  /** Stop all timers for a given user */
  '/api/v3/time_tracking/stop': new _routeClasses.RoutePostForUseMutator(),
  /** Stops all timers for a given move event */
  '/api/v3/time_tracking/stop_for_move_event': new _routeClasses.RoutePostForUseMutator()
};
const trainingRoutes = {
  /** Get all training objects. */
  '/api/v3/training/all': new _routeClasses.RoutePostForUseModel({
    model: 'training'
  }),
  /** Create a new training object. */
  '/api/v3/training/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single training object by id. */
  '/api/v3/training/get': new _routeClasses.RoutePostForUseModel({
    model: 'training',
    filter: (trainings, params) => trainings.filter(t => t.id === params.id),
    tryFilter: true
  }),
  /** Get several training objects by id. */
  '/api/v3/training/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'training',
    filter: (trainings, params) => trainings.filter(t => params.ids.some(id => t.id === id)),
    key: "ids"
  }),
  /** Retrieve information about which users require each training, and when. */
  '/api/v3/training/required': new _routeClasses.RoutePost(),
  /** Update a training object. */
  '/api/v3/training/update': new _routeClasses.RoutePostForUseMutator()
};
const trainingCoveredInEventRoutes = {
  /** Get all TrainingCoveredInEvent objects. */
  '/api/v3/training_covered_in_event/all': new _routeClasses.RoutePostForUseModel({
    model: 'training_covered_in_event'
  }),
  /** Create a new TrainingCoveredInEvent object. */
  '/api/v3/training_covered_in_event/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a TrainingCoveredInEvent object. */
  '/api/v3/training_covered_in_event/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get several TrainingCoveredInEvent objects by training event id. */
  '/api/v3/training_covered_in_event/get_by_event_ids': new _routeClasses.RoutePostForUseModel({
    model: 'training_covered_in_event',
    filter: (training_covered_in_events, params) => training_covered_in_events.filter(t => params.training_event_ids.some(id => id === t.training_event_id))
  }),
  /** Update a TrainingCoveredInEvent object. */
  '/api/v3/training_covered_in_event/update': new _routeClasses.RoutePostForUseMutator()
};
const trainingEventRoutes = {
  /** Get all training events. */
  '/api/v3/training_event/all': new _routeClasses.RoutePostForUseModel({
    model: 'training_event'
  }),
  /** Create a new training event. */
  '/api/v3/training_event/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get a training event by id. */
  '/api/v3/training_event/get': new _routeClasses.RoutePostForUseModel({
    model: 'training_event',
    filter: (training_events, params) => training_events.filter(event => event.id === params.id),
    tryFilter: true
  }),
  /** Get several training events by id. */
  '/api/v3/training_event/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'training_event',
    filter: (training_events, params) => training_events.filter(event => params.ids.some(id => event.id === id)),
    key: "ids"
  }),
  /** Get training events in a range of dates. */
  '/api/v3/training_event/get_in_range': new _routeClasses.RoutePostForUseModel({
    model: 'training_event',
    filter: (training_events, params) => training_events.filter(event => {
      return event.scheduled_date != null && event.scheduled_date >= params.start_timestamp && event.scheduled_date < params.end_timestamp;
    })
  }),
  /** Get all training events that are open (draft or scheduled). */
  '/api/v3/training_event/get_open': new _routeClasses.RoutePostForUseModel({
    model: 'training_event',
    filter: training_events => training_events.filter(event => (event.status === "draft" || event.status === "scheduled") && event.deleted === 0)
  }),
  /** Update a training event. */
  '/api/v3/training_event/update': new _routeClasses.RoutePostForUseMutator()
};
const trainingEventAttendeeRoutes = {
  /** Get all TrainingEventAttendee objects. */
  '/api/v3/training_event_attendee/all': new _routeClasses.RoutePostForUseModel({
    model: 'training_event_attendee'
  }),
  /** Create a new TrainingEventAttendee object. */
  '/api/v3/training_event_attendee/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a TrainingEventAttendee object. */
  '/api/v3/training_event_attendee/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get a TrainingEventAttendee object by id. */
  '/api/v3/training_event_attendee/get': new _routeClasses.RoutePostForUseModel({
    model: 'training_event_attendee',
    filter: (training_event_attendees, params) => training_event_attendees.filter(t => t.id === params.id),
    tryFilter: true
  }),
  /** Gets the TrainingEventAttendee objects that match the given training event ids. */
  '/api/v3/training_event_attendee/get_by_event_ids': new _routeClasses.RoutePostForUseModel({
    model: 'training_event_attendee',
    filter: (training_event_attendees, params) => training_event_attendees.filter(t => params.training_event_ids.some(event_id => event_id === t.training_event_id))
  }),
  /** Gets the TrainingEventAttendee objects that match the given user id. */
  '/api/v3/training_event_attendee/get_for_user': new _routeClasses.RoutePostForUseModel({
    model: 'training_event_attendee',
    filter: (training_event_attendees, params) => training_event_attendees.filter(t => t.user_id === params.user_id)
  }),
  /** Update a TrainingEventAttendee object. */
  '/api/v3/training_event_attendee/update': new _routeClasses.RoutePostForUseMutator()
};
const trainingGroupRoutes = {
  /** Gets all TrainingGroup objects. */
  '/api/v3/training_group/all': new _routeClasses.RoutePostForUseModel({
    model: 'training_group'
  }),
  /** Create a new TrainingGroup object. */
  '/api/v3/training_group/create': new _routeClasses.RoutePostForUseMutator(),
  /** Gets a single TrainingGroup object by id. */
  '/api/v3/training_group/get': new _routeClasses.RoutePostForUseModel({
    model: 'training_group',
    filter: (training_groups, params) => training_groups.filter(group => group.id === params.id),
    tryFilter: true
  }),
  /** Gets several TrainingGroup objects by id. */
  '/api/v3/training_group/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'training_group',
    filter: (training_groups, params) => training_groups.filter(group => params.ids.some(id => id === group.id)),
    key: 'ids'
  }),
  /** Update a TrainingGroup object. */
  '/api/v3/training_group/update': new _routeClasses.RoutePostForUseMutator()
};
const trainingGroupRequirementRoutes = {
  /** Get all TrainingGroupRequirement objects. */
  '/api/v3/training_group_requirement/all': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_requirement'
  }),
  /** Create a new TrainingGroupRequirement object. */
  '/api/v3/training_group_requirement/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a TrainingGroupRequirement object. */
  '/api/v3/training_group_requirement/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single TrainingGroupRequirement object by id. */
  '/api/v3/training_group_requirement/get': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_requirement',
    filter: (training_group_requirements, params) => training_group_requirements.filter(r => r.id === params.id),
    tryFilter: true
  }),
  /** Get the TrainingGroupRequirement objects that match a training group id */
  '/api/v3/training_group_requirement/get_for_group': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_requirement',
    filter: (training_group_requirements, params) => training_group_requirements.filter(r => r.training_group_id === params.training_group_id)
  }),
  /** Get the TrainingGroupRequirement objects that match a list of training group ids */
  '/api/v3/training_group_requirement/get_for_groups': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_requirement',
    filter: (training_group_requirements, params) => training_group_requirements.filter(r => params.training_group_ids.some(id => id === r.training_group_id)),
    key: 'training_group_ids'
  }),
  /** Get the TrainingGroupRequirement objects that match a training id */
  '/api/v3/training_group_requirement/get_for_training': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_requirement',
    filter: (training_group_requirements, params) => training_group_requirements.filter(r => r.training_id === params.training_id)
  }),
  /** Update a TrainingGroupRequirement object. */
  '/api/v3/training_group_requirement/update': new _routeClasses.RoutePostForUseMutator()
};
const trainingGroupUserRoutes = {
  /** Get all TrainingGroupUser objects. */
  '/api/v3/training_group_user/all': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_user'
  }),
  /** Create a new TrainingGroupUser object. */
  '/api/v3/training_group_user/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a TrainingGroupUser object. */
  '/api/v3/training_group_user/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single TrainingGroupUser object by id. */
  '/api/v3/training_group_user/get': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_user',
    filter: (training_group_users, params) => training_group_users.filter(u => u.id === params.id),
    tryFilter: true
  }),
  /** Gets the TrainingGroupUser objects that match the given training group id. */
  '/api/v3/training_group_user/get_for_group': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_user',
    filter: (training_group_users, params) => training_group_users.filter(u => u.training_group_id === params.training_group_id)
  }),
  /** Gets the TrainingGroupUser objects that match the given user id. */
  '/api/v3/training_group_user/get_for_user': new _routeClasses.RoutePostForUseModel({
    model: 'training_group_user',
    filter: (training_group_users, params) => training_group_users.filter(u => u.user_id === params.user_id)
  }),
  /** Update a TrainingGroupUser object. */
  '/api/v3/training_group_user/update': new _routeClasses.RoutePostForUseMutator()
};
const transactionRoutes = {
  /** Approve the transaction with the given id. */
  '/api/v3/transaction/approve': new _routeClasses.RoutePostForUseMutator(),
  /** Create a new transaction with optional transaction items. */
  '/api/v3/transaction/create': new _routeClasses.RoutePostForUseMutator(),
  /** Retrieve all transactions for a given customer */
  '/api/v3/transaction/get_for_customer': new _routeClasses.RoutePostForUseModel({
    model: 'transaction',
    filter: (transactions, params) => transactions.filter(t => t.customer_id === params.customer_id)
  }),
  /** Retrieve transactions with specified IDs. */
  '/api/v3/transaction/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'transaction',
    filter: (transactions, params) => transactions.filter(t => params.ids.includes(t.id)),
    key: 'ids'
  }),
  /** Gets the earliest allowed void date for a transaction */
  '/api/v3/transaction/get_minimum_void_date': new _routeClasses.RoutePost(),
  /** Update a transaction and optionally its transaction items. */
  '/api/v3/transaction/update': new _routeClasses.RoutePostForUseMutator(),
  /** Void the transaction with the given id. */
  '/api/v3/transaction/void': new _routeClasses.RoutePostForUseMutator(),
  '/api/v2/transaction/detail': new _routeClasses.RoutePost()
};
const transactionItemRoutes = {
  /** Retrieve transaction items for the given transaction.*/
  '/api/v3/transaction_item/get_for_transaction': new _routeClasses.RoutePostForUseModel({
    model: 'transaction_item',
    filter: (transactionItems, params) => transactionItems.filter(ti => ti.transaction_id === params.transaction_id)
  })
};
const transactionOldRoutes = {
  /** Retrieve all transactions for an order. */
  '/api/v3/transaction_old/for_order': new _routeClasses.RoutePostForUseModel({
    model: 'transaction_old',
    filter: (attachments, params) => attachments.filter(a => a.order_id === params.order_id)
  })
};
const tubeLaserJobRoutes = {
  /** Get all outstanding tube laser jobs. */
  '/api/v3/tube_laser_job/all': new _routeClasses.RoutePostForUseModel({
    model: 'tube_laser_job'
  }),
  /** Create a new tube laser job. */
  '/api/v3/tube_laser_job/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single tube laser job by id. */
  '/api/v3/tube_laser_job/get': new _routeClasses.RoutePostForUseModel({
    model: 'tube_laser_job',
    filter: (tube_laser_jobs, params) => tube_laser_jobs.filter(job => job.id === params.id),
    tryFilter: true
  }),
  /** Gets all jobs with the provided statuses */
  '/api/v3/tube_laser_job/get_for_statuses': new _routeClasses.RoutePostForUseModel({
    model: 'tube_laser_job',
    filter: (tube_laser_jobs, params) => tube_laser_jobs.filter(job => params.statuses.includes(job.status))
  }),
  /** Searches for jobs based on filters and returns a paginated result. */
  '/api/v3/tube_laser_job/get_paginated': new _routeClasses.RoutePost(),
  /**  Finishes a tube laser job, creates parts in the pipeline, and updates inventory. */
  '/api/v3/tube_laser_job/finish': new _routeClasses.RoutePostForUseMutator(),
  /** Swaps two loaded jobs. */
  '/api/v3/tube_laser_job/swap_in_queue': new _routeClasses.RoutePostForUseMutator(),
  /** Update a tube laser job. */
  '/api/v3/tube_laser_job/update': new _routeClasses.RoutePostForUseMutator()
};
const tubeLaserJobItemInventoryRoutes = {
  /** Adds an item inventory to a tube laser job. */
  '/api/v3/tube_laser_job_item_inventory/create': new _routeClasses.RoutePostForUseMutator(),
  /** Gets all inventory items for a list of tube laser jobs. */
  '/api/v3/tube_laser_job_item_inventory/get_all_for_jobs': new _routeClasses.RoutePostForUseModel({
    model: 'tube_laser_job_item_inventory',
    filter: (items, params) => items.filter(item => params.tube_laser_job_ids.includes(item.tube_laser_job_id)),
    key: 'tube_laser_job_ids'
  }),
  /** Removes an item inventory for a tube laser job. */
  '/api/v3/tube_laser_job_item_inventory/remove': new _routeClasses.RoutePostForUseMutator(),
  /** Updates an item inventory for a tube laser job. */
  '/api/v3/tube_laser_job_item_inventory/update': new _routeClasses.RoutePostForUseMutator()
};
const tubeLaserJobOrderPartRoutes = {
  /** Adds an order part to a laser tube job. */
  '/api/v3/tube_laser_job_order_part/create': new _routeClasses.RoutePostForUseMutator(),
  /** Gets all order parts for a tube laser job. */
  '/api/v3/tube_laser_job_order_part/get_for_job': new _routeClasses.RoutePostForUseModel({
    model: 'tube_laser_job_order_part',
    filter: (parts, params) => parts.filter(part => part.tube_laser_job_id === params.tube_laser_job_id)
  }),
  /** Removes an order part from a laser tube job. */
  '/api/v3/tube_laser_job_order_part/remove': new _routeClasses.RoutePostForUseMutator(),
  /** Updates an order part for a tube laser job. */
  '/api/v3/tube_laser_job_order_part/update': new _routeClasses.RoutePostForUseMutator()
};
const tubeLaserJobRemainderRoutes = {
  /** Create a new tube laser job remainder. */
  '/api/v3/tube_laser_job_remainder/create': new _routeClasses.RoutePostForUseMutator(),
  /** Get all tube laser job remainders by a given tube laser job id */
  '/api/v3/tube_laser_job_remainder/get_for_job': new _routeClasses.RoutePostForUseModel({
    model: 'tube_laser_job_remainder',
    filter: (tube_laser_jobs, params) => tube_laser_jobs.filter(job => job.tube_laser_job_id === params.tube_laser_job_id)
  }),
  /** Removes a tube laser job remainder. */
  '/api/v3/tube_laser_job_remainder/remove': new _routeClasses.RoutePostForUseMutator(),
  /** Update a tube laser job remainder. */
  '/api/v3/tube_laser_job_remainder/update': new _routeClasses.RoutePostForUseMutator()
};
const userRoutes = {
  /** Gets all users. */
  '/api/v3/user/all': new _routeClasses.RoutePostForUseModel({
    model: 'user'
  }),
  /** Get a single user by id. */
  '/api/v3/user/get': new _routeClasses.RoutePostForUseModel({
    model: 'user',
    filter: (users, params) => users.filter(user => user.id === params.id),
    tryFilter: true
  }),
  /** Return the currently authenticated user. */
  '/api/v3/user/get_by_token': new _routeClasses.RoutePost(),
  /** Authenticate with an admin username and password, or a pin. */
  '/api/v1/user/login': new _routeClasses.RoutePost(),
  /** Update a user. */
  '/api/v3/user/update': new _routeClasses.RoutePostForUseMutator()
};
const vendorRoutes = {
  /** Get all vendors. */
  '/api/v3/vendor/all': new _routeClasses.RoutePostForUseModel({
    model: 'vendor'
  }),
  /** Create a new vendor */
  '/api/v3/vendor/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete vendor by id */
  '/api/v3/vendor/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Get a single vendor by id. */
  '/api/v3/vendor/get': new _routeClasses.RoutePostForUseModel({
    model: 'vendor',
    filter: (vendor, params) => vendor.filter(vendor => vendor.id === params.id),
    tryFilter: true
  }),
  /** Retrieve several vendors by id. */
  '/api/v3/vendor/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'vendor',
    filter: (vendors, params) => vendors.filter(vendor => params.ids.includes(vendor.id)),
    key: 'ids'
  }),
  /** Update a vendor. */
  '/api/v3/vendor/update': new _routeClasses.RoutePostForUseMutator()
};
const vendorItemRoutes = {
  /** Get all vendor items. */
  '/api/v3/vendor_item/all': new _routeClasses.RoutePostForUseModel({
    model: 'vendor_item'
  }),
  /** Create a new vendor item */
  '/api/v3/vendor_item/create': new _routeClasses.RoutePostForUseMutator(),
  /** Gets all vendor items for the given vendor */
  '/api/v3/vendor_item/for_vendor': new _routeClasses.RoutePostForUseModel({
    model: 'vendor_item',
    filter: (vendorItems, params) => vendorItems.filter(vendorItem => params.id == vendorItem.vendor_id)
  }),
  /** Get a single vendor item by id. */
  '/api/v3/vendor_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'vendor_item',
    filter: (vendorItems, params) => vendorItems.filter(vendorItem => vendorItem.id === params.id),
    tryFilter: true
  }),
  /** Retrieve several vendor items by item_id. */
  '/api/v3/vendor_item/get_for_item': new _routeClasses.RoutePostForUseModel({
    model: 'vendor_item',
    filter: (vendorItems, params) => vendorItems.filter(vendorItem => params.item_id == vendorItem.item_id)
  }),
  /** Retrieve vendor items for the given material sheet. */
  '/api/v3/vendor_item/get_for_sheet': new _routeClasses.RoutePost(),
  /** Retrieve several vendor items by id. */
  '/api/v3/vendor_item/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'vendor_item',
    filter: (vendorItems, params) => vendorItems.filter(vendorItem => params.ids.includes(vendorItem.id)),
    key: 'ids'
  }),
  /** Update a vendor item. */
  '/api/v3/vendor_item/update': new _routeClasses.RoutePostForUseMutator()
};
const versionsRoutes = {
  /** Get the current version of the specified application. */
  '/api/v2/versions': new _routeClasses.RoutePost()
};
const workOrderRoutes = {
  /** Create a new WorkOrder. */
  '/api/v3/work_order/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a WorkOrder. */
  '/api/v3/work_order/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Finds work orders based on a set of filters. */
  '/api/v3/work_order/find': new _routeClasses.RoutePostForUseModel({
    model: 'work_order',
    filter: (workOrders, params) => workOrders.filter(workOrder => {
      var _statuses;
      let statuses = params.statuses;
      if ((_statuses = statuses) !== null && _statuses !== void 0 && _statuses.includes('all')) {
        // Don't filter by status at all
      } else {
        var _statuses2, _statuses3;
        // If all_open is included, replace entire array with all open statuses
        if ((_statuses2 = statuses) !== null && _statuses2 !== void 0 && _statuses2.includes('all_open')) statuses = ['released', 'hold_feedback', 'hold_material', 'hold_review', 'hold_payment'];
        if (!((_statuses3 = statuses) !== null && _statuses3 !== void 0 && _statuses3.includes(workOrder.status))) return false;
      }
      return true;
    })
  }),
  /** Retrieve a WorkOrder by id. */
  '/api/v3/work_order/get': new _routeClasses.RoutePostForUseModel({
    model: 'work_order',
    filter: (workOrders, params) => workOrders.filter(workOrder => workOrder.id === params.id),
    tryFilter: true
  }),
  /** Get multiple WorkOrders by id. */
  '/api/v3/work_order/get_ids': new _routeClasses.RoutePostForUseModel({
    model: 'work_order',
    filter: (workOrders, params) => workOrders.filter(workOrder => params.ids.includes(workOrder.id)),
    key: 'ids'
  }),
  /** Get a set of WorkOrders based on a list of order ids. */
  '/api/v3/work_order/get_for_orders': new _routeClasses.RoutePostForUseModel({
    model: 'work_order',
    filter: (workOrders, params) => workOrders.filter(workOrder => params.order_ids.includes(workOrder.order_id)),
    key: 'order_ids'
  }),
  /** Search for work_orders and return a paginated result. */
  '/api/v3/work_order/paginated_search': new _routeClasses.RoutePost(),
  /** Update a WorkOrder. */
  '/api/v3/work_order/update': new _routeClasses.RoutePostForUseMutator()
};
const workOrderItemRoutes = {
  /** Create a new WorkOrderItem. */
  '/api/v3/work_order_item/create': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a WorkOrderItem. */
  '/api/v3/work_order_item/delete': new _routeClasses.RoutePostForUseMutator(),
  /** Find WorkOrderItems based on a set of filters. */
  '/api/v3/work_order_item/find': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => workOrderItems.filter(workOrderItem => {
      if (params.work_order_id != null && workOrderItem.work_order_id !== params.work_order_id) return false;
      if (params.item_id != null && workOrderItem.item_id !== params.item_id) return false;
      if (params.work_order_ids != null && !params.work_order_ids.includes(workOrderItem.work_order_id)) return false;
      if (params.item_ids != null && !params.item_ids.includes(workOrderItem.item_id)) return false;
      return true;
    })
  }),
  /** Retrieve all WorkOrderItems for a WorkOrder. */
  '/api/v3/work_order_item/for_work_order': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => workOrderItems.filter(workOrderItem => workOrderItem.work_order_id === params.work_order_id)
  }),
  /** Retrieve a WorkOrderItem by id. */
  '/api/v3/work_order_item/get': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => workOrderItems.filter(workOrderItem => workOrderItem.id === params.id),
    tryFilter: true
  }),
  /** Retrieves WorkOrderItems with the given internal_ids. Case-insensitive. */
  '/api/v3/work_order_item/get_by_internal_ids': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => {
      let allLowercase = params.internal_ids.map(id => id.toLowerCase());
      return workOrderItems.filter(workOrderItem => allLowercase.includes(workOrderItem.internal_id.toLowerCase()));
    }
  }),
  /** Retrieve all WorkOrderItems for a list of items. */
  '/api/v3/work_order_item/get_for_items': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => workOrderItems.filter(workOrderItem => params.item_ids.includes(workOrderItem.item_id)),
    key: 'item_ids'
  }),
  /** Gets all work order items for the given process. */
  '/api/v3/work_order_item/get_for_process': new _routeClasses.RoutePost(),
  /** Retrieve all WorkOrderItems for a set of WorkOrder.s */
  '/api/v3/work_order_item/get_for_work_orders': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => workOrderItems.filter(workOrderItem => params.work_order_ids.includes(workOrderItem.work_order_id)),
    key: 'work_order_ids'
  }),
  /** Gets all work order items in released work orders for the given material sheet. */
  '/api/v3/work_order_item/get_released_for_material_sheet': new _routeClasses.RoutePost(),
  /** Retrieve several WorkOrderItems by id. */
  '/api/v3/work_order_item/ids': new _routeClasses.RoutePostForUseModel({
    model: 'work_order_item',
    filter: (workOrderItems, params) => workOrderItems.filter(workOrderItem => params.ids.includes(workOrderItem.id)),
    key: 'ids'
  }),
  /** Update a WorkOrderItem. */
  '/api/v3/work_order_item/update': new _routeClasses.RoutePostForUseMutator()
};
const workstationRoutes = {
  /** Retrieve all workstations. */
  '/api/v3/workstation/all': new _routeClasses.RoutePostForUseModel({
    model: 'workstation'
  }),
  /** Retrieve all workstations in a given facility. */
  '/api/v3/workstation/for_facility': new _routeClasses.RoutePostForUseModel({
    model: 'workstation',
    filter: (workstations, params) => workstations.filter(w => w.facility_id === params.facility_id)
  }),
  /** Retrieve all workstations that can perform a given operation. */
  '/api/v3/workstation/for_operation': new _routeClasses.RoutePostForUseModel({
    model: 'workstation',
    filter: (workstations, params) => workstations.filter(w => w.operation_id === params.operation_id)
  }),
  /** Create a new workstation. */
  '/api/v3/workstation/create': new _routeClasses.RoutePostForUseMutator(),
  /** Update a workstation. */
  '/api/v3/workstation/update': new _routeClasses.RoutePostForUseMutator(),
  /** Delete a workstation. */
  '/api/v3/workstation/delete': new _routeClasses.RoutePostForUseMutator()
};

/**
 * All routes for OSH Cut's API that use the POST method.  
 */
const oshcutPostRoutes = exports.oshcutPostRoutes = {
  ...accAccountRoutes,
  ...accItemRoutes,
  ...accPurchaseOrderRoutes,
  ...accPurchaseOrderItemRoutes,
  ...accountStatementRoutes,
  ...addressRoutes,
  ...affiliateRoutes,
  ...arcifyRoutes,
  ...attachmentRoutes,
  ...bankTransactionRoutes,
  ...bomOperationRoutes,
  ...bomOperationItemRoutes,
  ...boxRoutes,
  ...clientLogRoutes,
  ...companyRoutes,
  ...companyExemptFormRoutes,
  ...couponRoutes,
  ...creditNotes,
  ...customerRoutes,
  ...customerUserRoutes,
  ...customerAccountLedgerRoutes,
  ...cutConditionRoutes,
  ...cutConditionFrequencyRoutes,
  ...databaseSchemaRoutes,
  ...earnedRevenueRoutes,
  ...eventRoutes,
  ...facilityRoutes,
  ...globalRoutes,
  ...hardwareRoutes,
  ...imagekitRoutes,
  ...itemInventoryRoutes,
  ...itemInventoryLocationRoutes,
  ...labelRoutes,
  ...labelPartRoutes,
  ...leadinPresetsRoutes,
  ...libraryPartRoutes,
  ...logRoutes,
  ...machineRoutes,
  ...machineStateAggregateRoutes,
  ...materialRoutes,
  ...materialSheetRoutes,
  ...materialSheetConditionRoutes,
  ...materialTubeRoutes,
  ...materialTypeRoutes,
  ...messageRoutes,
  ...moveEventDocumentRoutes,
  ...nestRoutes,
  ...nestingCacheRoutes,
  ...nestPartRoutes,
  ...nestPartPoseRoutes,
  ...nestSheetRoutes,
  ...nestSheetDenestRoutes,
  ...notesRoutes,
  ...nppDenestRoutes,
  ...operationRoutes,
  ...orderRoutes,
  ...orderCalendarRoutes,
  ...orderCostDetailRoutes,
  ...orderPartRoutes,
  ...partRoutes,
  ...partProgramRoutes,
  ...paymentMethodRoutes,
  ...paymentRoutes,
  ...permissionGroupRoutes,
  ...pipeStatusRoutes,
  ...powderRoutes,
  ...powderJobRoutes,
  ...powderPartRoutes,
  ...processRoutes,
  ...productionRoutes,
  ...qualityRoutes,
  ...remDenestRoutes,
  ...resourceLocks,
  ...salesInvoiceRoutes,
  ...salesInvoiceItemRoutes,
  ...scrapRatesRoutes,
  ...scrapRecordRoutes,
  ...settingsRoutes,
  ...shipmentRoutes,
  ...shipmentItemRoutes,
  ...shipmentLabelRoutes,
  ...shippingOrderRoutes,
  ...shippingOrderItemRoutes,
  ...syncCounterRoutes,
  ...tapsRoutes,
  ...taskRoutes,
  ...timeTrackingRoutes,
  ...trainingRoutes,
  ...trainingCoveredInEventRoutes,
  ...trainingEventRoutes,
  ...trainingEventAttendeeRoutes,
  ...trainingGroupRoutes,
  ...trainingGroupRequirementRoutes,
  ...trainingGroupUserRoutes,
  ...transactionRoutes,
  ...transactionItemRoutes,
  ...transactionOldRoutes,
  ...tubeLaserJobRoutes,
  ...tubeLaserJobItemInventoryRoutes,
  ...tubeLaserJobOrderPartRoutes,
  ...tubeLaserJobRemainderRoutes,
  ...userRoutes,
  ...vendorRoutes,
  ...vendorItemRoutes,
  ...versionsRoutes,
  ...workOrderRoutes,
  ...workOrderItemRoutes,
  ...workstationRoutes
};

// Set the `url` property of each route
for (const [url, route] of Object.entries(oshcutGetRoutes)) {
  route.url = url;
}
for (const [url, route] of Object.entries(oshcutPostRoutes)) {
  route.url = url;
}